import { useState, useEffect, useRef, useMemo, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import toast from "react-hot-toast";
import MainLayout from "../components/layout/MainLayout";
import { Button } from "../components/ui/button";
import {
  Search, Gift, Trophy, Sparkles, Play, Flame, Crown, 
  Clock, Heart, Gamepad2, ChevronRight, ChevronLeft, Star, Filter, X, Ticket, PartyPopper, Rocket, Timer,
  Fish, Zap, Award, TrendingUp, Coins
} from "lucide-react";
import { getBetsoftGameImage, GAME_NAME_OVERRIDES, GAME_CATEGORY_MAP, CATEGORY_IMAGES } from "../data/betsoftGameImages";
import GiveawayLobbyBanner from "../components/GiveawayLobbyBanner";
import WelcomeBonusBanner from "../components/WelcomeBonusBanner";
import Mascot from "../components/Mascot";

// ─── Constants ─────────────────────────────────────────────
const VIP_TIERS = [
  { name: "Bronze", minLevel: 1, color: "from-amber-700 to-amber-900", accent: "text-[#4d7cff]", icon: Award },
  { name: "Silver", minLevel: 10, color: "from-zinc-400 to-zinc-600", accent: "text-zinc-300", icon: Star },
  { name: "Gold", minLevel: 25, color: "from-yellow-500 to-amber-600", accent: "text-yellow-400", icon: Crown },
  { name: "Platinum", minLevel: 50, color: "from-cyan-400 to-blue-600", accent: "text-cyan-300", icon: Trophy },
  { name: "Diamond", minLevel: 100, color: "from-purple-400 to-pink-500", accent: "text-purple-300", icon: Sparkles },
];

const getVipTier = (level) => {
  for (let i = VIP_TIERS.length - 1; i >= 0; i--) {
    if (level >= VIP_TIERS[i].minLevel) return VIP_TIERS[i];
  }
  return VIP_TIERS[0];
};

const CATEGORIES = [
  { id: "all",        label: "All Games",     icon: Gamepad2 },
  { id: "featured",   label: "Featured",      icon: Crown,       badge: "PICKS" },
  { id: "shooting",   label: "Shooting Fish", icon: Rocket,      badge: "HOT" },
  { id: "top",        label: "TOP Games",     icon: Flame,       badge: "TOP" },
  { id: "popular",    label: "Popular",       icon: Flame,       badge: "HOT" },
  { id: "new",        label: "New Games",     icon: PartyPopper, badge: "NEW" },
  { id: "favorites",  label: "Favorites",     icon: Heart },
  { id: "slots",      label: "Arcade Games",  icon: Sparkles },
  { id: "jackroyals", label: "JackRoyals",    icon: Crown },
  { id: "crash",      label: "Crash",         icon: Rocket,      badge: "HOT" },
  { id: "table",      label: "Card Games",    icon: Crown },
];

const SLOT_SUBCATEGORIES = [
  { id: "all-slots", label: "All Games", icon: Sparkles },
  { id: "classic", label: "Classics", icon: Star },
  { id: "video-slots", label: "Featured", icon: Play },
  { id: "bonus-buy", label: "Bonus Games", icon: Gift },
  { id: "high-volatility", label: "High Reward", icon: Rocket },
  { id: "new-releases", label: "New Releases", icon: PartyPopper, badge: "NEW" },
];

const JACKROYALS_PROVIDERS = ["JackRoyals", "JackRoyals Original", "JackRoyals Originals"];
// KA Gaming is now public (launched Aug 2026) — included in the base list
// so every player sees the chip + can filter by KA. The admin-only variant
// is kept for legacy code paths but is now equivalent to the base list.
// Feb 2026: "3 Oaks" moved to admin-only chip (appended dynamically) so
// regular players no longer see the tab.
const PROVIDERS_BASE  = ["All", "Mascot", "KA Gaming", "JackRoyals", "Booming Games", "Betsoft", "Redwood", "Spinjoy"];

// Stable, module-level style refs for the category pill buttons.
// Inline-object style props create a NEW reference on every parent render,
// which on mobile combined with `transition-all` caused the active "All Games"
// pill's box-shadow to flicker every time the lobby re-rendered for any
// reason (balance refresh, jackpot tick, etc.). These constants stop that.
const CATEGORY_BTN_ACTIVE_STYLE = { background: '#4d7cff', boxShadow: '0 0 12px rgba(77,124,255,0.2)' };
const CATEGORY_BTN_ACTIVE_STYLE_DESKTOP = { background: '#4d7cff', boxShadow: '0 0 15px rgba(77,124,255,0.2)' };
const CATEGORY_BTN_INACTIVE_STYLE = {};
// KA Gaming is admin-only — appended dynamically when user is admin.
// Feb 2026: "3 Oaks" removed from static list; appended conditionally
// when the viewer is a real admin (see PROVIDERS assembly below).
const PROVIDERS_ADMIN = ["All", "Mascot", "KA Gaming", "JackRoyals", "Booming Games", "Betsoft", "Redwood", "Spinjoy"];

// Resolve a thumbnail for a curated (featured/top) entry. Some early-pinned
// Betsoft games were stored with a `download.betsoftgaming.com/UI/games/...`
// CDN URL that returns 404 — map those through getBetsoftGameImage at render time.
const BACKEND_HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
// ── KA Gaming thumbnail overrides ──────────────────────────
// The KA CDN serves the FULL landscape marketing shots for these 5 recently
// launched titles (huge blank sky around a tiny centered game icon). Those
// look terrible in the square lobby tile. We ship our own tight 1000x1000
// center-cropped WebP covers under /public/ka-covers/ and override the KA
// CDN URL by gameId here. Adding a new title? Drop the WebP into
// /public/ka-covers AND /backend/frontend_build/ka-covers and add its
// gameId → filename mapping below.
const KA_THUMB_OVERRIDES = {
  GoldenBeansScratch: "/ka-covers/GoldenBeansScratch.webp",
  SkyboundPirates:    "/ka-covers/SkyboundPirates.webp",
  FireHit2:           "/ka-covers/FireHit2.webp",
  HyakkiYoichi:       "/ka-covers/HyakkiYoichi.webp",
  RedGhostBride:      "/ka-covers/RedGhostBride.webp",
};

const resolveCuratedThumb = (g) => {
  if (!g) return "";
  const prov = (g.provider || "").toLowerCase();
  // Accept both `thumbnail` (curated / Betsoft / Redwood) and
  // `image` (Mascot / Booming feeds). Whichever is populated wins.
  const t = g.thumbnail || g.image || "";
  const gid = String(g.game_id || g.gameId || g.id || "");

  // ── KA Gaming ────────────────────────────────────────────────
  // KA's public CDN 404s on direct GETs without a KA-authorized referer,
  // so we ALWAYS route through the site's own /api/ka/thumb/ proxy which
  // handles auth + region routing and 302s to a real PNG.
  if (prov === "ka" || prov === "ka gaming" || prov === "kagaming") {
    if (KA_THUMB_OVERRIDES[gid]) return KA_THUMB_OVERRIDES[gid];
    if (t.startsWith("/api/ka/thumb/")) return t;
    const bareId = gid.replace(/^ka_/i, "");
    if (bareId) return `/api/ka/thumb/${bareId}`;
    return t;
  }

  // ── Redwood ─────────────────────────────────────────────
  // Backend serves the local PNG pack under /api/game-assets/petersons/
  // (populated by the P&S catalog import). Fallback synthesizes that
  // path from the game id when the row didn't get a thumbnail persisted.
  if (prov === "petersons" || prov === "peter & sons" || prov === "peter and sons") {
    if (t) return t;
    const slug = gid.replace(/^petersons_/i, "").replace(/_/g, "-").toLowerCase();
    return slug ? `/api/game-assets/petersons/${slug}.png` : "";
  }

  // ── Booming Games ────────────────────────────────────────────
  // Curated entries from `db.featured_games` carry a full CDN URL or the
  // local `/api/game-assets/booming/...` path. But auto-injected entries
  // in the TOP Games row (from `/api/new-game-launches`) don't include a
  // thumbnail — synthesize the local pack path from the boom_ prefix
  // so freshly-launched Booming titles never render as a black card.
  if (prov === "booming games" || prov === "booming") {
    if (t) return t;
    // Explicit mapping for titles whose provider ID doesn't match the
    // slug convention (e.g. mongo ObjectIds instead of `boom_<slug>`).
    const _boomingDirect = {
      "6a59e7c49714218fbf00046a": "/api/game-assets/booming/greedy-for-gold-power-hit-10000-v2.png?v=2",
    };
    if (_boomingDirect[gid]) return _boomingDirect[gid];
    const slug = gid.replace(/^boom_/i, "").replace(/_/g, "-").toLowerCase();
    return slug ? `/api/game-assets/booming/${slug}.png` : "";
  }

  // ── Betsoft ──────────────────────────────────────────────────
  // Original bug: some early-pinned Betsoft rows shipped with a
  // `download.betsoftgaming.com/UI/games/...` URL that 404s. Empty
  // strings hit the same code path so we always try the local catalog.
  if (prov === "betsoft") {
    const broken = !t || t.includes("download.betsoftgaming.com");
    if (broken) {
      const numericId = gid.replace(/^betsoft_/, "");
      const rel = getBetsoftGameImage(numericId, g.name);
      if (!rel) return "/api/game-assets/games/betsoft/_placeholder.jpg";
      if (rel.startsWith("http://") || rel.startsWith("https://")) return rel;
      if (rel.startsWith("/")) return `${BACKEND_HOST}${rel}`;
      return rel;
    }
  }
  return t;
};

// ─── Isolated Jackpot Ticker ───────────────────────────────
// Owns its own 1-second tick so it ONLY re-renders itself, not the entire
// GameLobby. Previously the ticker lived in the parent state, which forced a
// full lobby re-render (and rebuild of the 1000+ game arrays) every second —
// visible as constant flicker on mobile.
const TickingJackpot = memo(function TickingJackpot({ base, step }) {
  const [v, setV] = useState(base);
  // Reset display value whenever the API-sourced base value changes.
  useEffect(() => { setV(base); }, [base]);
  useEffect(() => {
    if (!base || base <= 0) return;
    const id = setInterval(() => setV((prev) => prev + step), 1000);
    return () => clearInterval(id);
  }, [base, step]);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
});



// ─── Shared Utility ────────────────────────────────────────
// Shooting Games — arcade cannon-style shooters where you aim at fish, sharks,
// dragons, and mythic sea creatures (KA Gaming's "fish" gameType is the canonical
// signal; we also catch anything with clear shooter naming from other providers).
const SHOOTER_KEYWORDS = [
  "shark", "hunter", "shoot", "shooter", "cannon", "battle", "war ",
  "dragon slayer", "dragon boom", "dragon hunter", "iron chicken",
  "monster hunter", "gunslinger", "sniper", "target",
];
const SHOOTER_NAME_HITS = (name) => SHOOTER_KEYWORDS.some(k => name.includes(k));

const classifyGame = (game) => {
  const name = (game.name || game.gameName || "").toLowerCase();
  const cat = (game.category || "").toLowerCase();
  const suite = (game.suiteName || "").toLowerCase();
  const kaType = (game.gameType || "").toLowerCase();  // KA feed sets this
  // BACKEND-AUTHORITATIVE for KA games (Aug 2026): the KA router now
  // classifies every game as slots / shooting / live / table / arcade before
  // sending it here. Respect that classification verbatim so admins can
  // hand-correct a KA title via /api/ka/admin/seed-game and see it move
  // instantly, without waiting for the frontend keyword list to catch up.
  if (game.isKA) {
    if (cat === "shooting") return "shooting";
    if (cat === "live") return "table";           // Live dealer sits in Table tab
    if (cat === "table") return "table";
    if (cat === "arcade") return "table";         // Arcade / mini-games in Table for now
    // else fall through to keyword classifier below
  }
  // Shooter classification runs FIRST (before table's "war" match) so
  // "Zombie War", "Cannon Wars" etc. don't get miscategorized.
  if (kaType === "fish" || kaType === "shooting" || kaType === "shooter") return "shooting";
  if (SHOOTER_NAME_HITS(name)) return "shooting";
  // Table/arcade games (higher-priority than slots)
  if (cat === "table" || cat === "blackjack" || cat === "poker" || cat === "roulette" || cat === "baccarat") return "table";
  if (suite.includes("table") || suite.includes("poker") || suite.includes("blackjack") || suite.includes("roulette") || suite.includes("baccarat")) return "table";
  if (name.includes("blackjack") || name.includes("roulette") || name.includes("baccarat") || name.includes("poker") || name.includes("craps") || name.includes("hold'em") || name.includes("holdem") || name.includes("casino hold") || name.includes("plinko") || name.includes("keno") || name.includes("bingo") || name.includes("dice") || name.includes("sic bo") || name.includes("hi-lo") || name.includes("hi lo") || name.includes("war")) return "table";
  // Fish games (slot-style fishing themed) — merged with Shooting Fish tab
  // (Aug 2026) so KA's entire fishing/shooter catalog lives in one place.
  if (cat === "fish") return "shooting";
  if (name.includes("fish") || name.includes("ocean king") || name.includes("fishing") || name.includes("sea hunter") || name.includes("aqua")) return "shooting";
  // Crash games
  if (cat === "crash") return "crash";
  if (name.includes("crash") || name.includes("aviator")) return "crash";
  return cat || "slots";
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ─── Grid Section (Reference-style layout) ───────────────
const GridSection = ({ title, icon: Icon, games, totalCount, onGameClick, onViewAll, onFavorite, favorites = [], visibleCount = 5 }) => {
  if (!games || games.length === 0) return null;
  const displayGames = games.slice(0, visibleCount);
  const count = totalCount || games.length;

  return (
    <div className="mb-8" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-[#4d7cff]" />}
          <h3 className="text-base font-bold text-white tracking-tight">{title}</h3>
        </div>
        {onViewAll && (
          <button onClick={onViewAll} className="text-emerald-400 text-sm font-semibold flex items-center gap-1 hover:underline" data-testid={`view-all-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            View All <span className="text-emerald-300">{count} Games</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {displayGames.map((game, idx) => (
          <GridGameCard
            key={game.id || idx}
            game={game}
            onClick={() => onGameClick(game)}
            onFavorite={onFavorite}
            isFavorite={favorites?.includes(game.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Check if a game is currently inside the "NEW" launch window (driven by /api/games/new-launches)
const isNewLaunch = (game) => {
  if (typeof window === "undefined" || !window.__jrNewLaunches) return false;
  const provider = game.isMascot ? "mascot" : game.isKA ? "ka" : game.isBooming ? "booming" : game.isBetsoft ? "betsoft" : game.isPetersons ? "petersons" : game.isSpinjoy ? "spinjoy" : "original";
  const set = window.__jrNewLaunches[provider];
  if (!set) return false;
  // Provider games carry their raw ID under .gameId; fall back to .id otherwise
  const id = String(game.gameId ?? game.id ?? "");
  return set.has(id);
};

// Return the launch timestamp (ms) for a game if it's currently in the NEW
// window, else 0. Used to sort New Games / Top Games so the freshest release
// is always at the #1 slot. Falls back to game.releasedAt when the launches
// registry has no explicit date (Spinjoy games carry releasedAt from the
// export API).
const newLaunchTimestamp = (game) => {
  try {
    if (typeof window !== "undefined" && window.__jrNewLaunchDates) {
      const provider = game.isMascot ? "mascot" : game.isKA ? "ka" : game.isBooming ? "booming" : game.isBetsoft ? "betsoft" : game.isPetersons ? "petersons" : game.isSpinjoy ? "spinjoy" : "original";
      const providerMap = window.__jrNewLaunchDates[provider];
      const id = String(game.gameId ?? game.id ?? "");
      if (providerMap && providerMap[id]) return providerMap[id];
    }
    if (game.releasedAt) return Date.parse(game.releasedAt) || 0;
    if (game.releaseDate) return Date.parse(game.releaseDate) || 0;
    if (game.created_at) return Date.parse(game.created_at) || 0;
  } catch (_e) {}
  return 0;
};

// ─── SmartImg: shows JR crown placeholder if image fails OR takes >2s ─
// Prevents black boxes when a provider CDN is slow or blocks the request
// (Feb 2026: added after Redwood + Booming CDNs started 502'ing).
// The 10s hard-fail is used by the launch iframe wrapper, not here.
const JR_PLACEHOLDER = "/jr-crown.jpeg";
const SmartImg = ({ src, alt, className = "", objectFit = "cover", providerLocalFallback }) => {
  const [displaySrc, setDisplaySrc] = useState(src || JR_PLACEHOLDER);
  const [loaded, setLoaded] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);
  useEffect(() => {
    setDisplaySrc(src || JR_PLACEHOLDER);
    setLoaded(false);
    setTriedFallback(false);
    if (!src) return;
    // 2s soft timeout — if the image hasn't fired onLoad by then, swap to
    // the provider's local fallback (or JR crown as last resort). The
    // real image can still finish loading; we just stop showing a black
    // box in the meantime.
    const t = setTimeout(() => {
      setLoaded(prev => {
        if (!prev) {
          setDisplaySrc(providerLocalFallback || JR_PLACEHOLDER);
          return false;
        }
        return prev;
      });
    }, 2000);
    return () => clearTimeout(t);
  }, [src, providerLocalFallback]);
  return (
    <img
      src={displaySrc}
      alt={alt}
      className={`${className} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-70"}`}
      style={{ objectFit }}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      onError={(e) => {
        if (!triedFallback && providerLocalFallback && e.currentTarget.src !== providerLocalFallback) {
          setTriedFallback(true);
          setDisplaySrc(providerLocalFallback);
          return;
        }
        if (e.currentTarget.src !== JR_PLACEHOLDER) {
          setDisplaySrc(JR_PLACEHOLDER);
        }
      }}
    />
  );
};

// ─── Grid Game Card (provider label below image) ──────────
const GridGameCard = ({ game, onClick, onFavorite, isFavorite }) => {
  // Trust the backend `provider` string first — this prevents mislabeling when
  // a game happens to carry multiple isX flags (e.g. a Betsoft title that
  // was spread through the Mascot map upstream). Fall back to isX flags only
  // if no explicit provider name was set.
  const prov = (game.provider || "").toLowerCase();
  let providerLabel;
  if      (prov.includes("mascot"))    providerLabel = "MASCOT";
  else if (prov.includes("ka"))        providerLabel = "KA GAMING";
  else if (prov.includes("booming"))   providerLabel = "BOOMING";
  else if (prov.includes("betsoft"))   providerLabel = "BETSOFT";
  else if (prov.includes("peter"))     providerLabel = "REDWOOD";
  else if (game.isMascot)              providerLabel = "MASCOT";
  else if (game.isKA)                  providerLabel = "KA GAMING";
  else if (game.isBooming)             providerLabel = "BOOMING";
  else if (game.isBetsoft)             providerLabel = "BETSOFT";
  else if (game.isPetersons)           providerLabel = "REDWOOD";
  else                                 providerLabel = "JACKROYALS";

  return (
    <div onClick={onClick} data-testid={`game-card-${game.id}`} className="group cursor-pointer">
      <div className="relative rounded-xl overflow-hidden bg-[#0c0c14] border border-white/[0.06] hover:border-emerald-500/40 transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]">
        <div className="aspect-square relative overflow-hidden">
          <img
            src={game.thumbnail || game.image}
            alt={game.name}
            className={`w-full h-full ${game.isKA ? "object-cover" : "object-contain"} transition-transform duration-500 group-hover:scale-105`}
            loading="lazy"
            onError={(e) => {
              // Tiered fallback chain so cards NEVER go blank (which previously
              // looked like flicker on mobile when many images failed at once).
              //   1) Betsoft: try the provider's CDN logo
              //   1) Mascot: try the local 500x500 icon mirror
              //   2) Any provider: swap to a local generic placeholder
              //   3) After tier 2: clear onerror to stop the loop, keep alt text
              const tier = e.target.dataset.fbTier || "0";
              if (tier === "0" && game.isBetsoft && game.gameId) {
                e.target.dataset.fbTier = "1";
                e.target.src = `https://swmd.bfrnd.net/media/games/${game.gameId}/${game.gameId}_logo.png`;
              } else if (tier === "0" && game.isMascot && (game.gameId || game.id)) {
                e.target.dataset.fbTier = "1";
                const gid = game.gameId || game.id;
                e.target.src = `/api/game-assets/mascot/icons_500x500/${gid}_500x500.png`;
              } else if (tier === "0" && game.isPetersons && game.id) {
                e.target.dataset.fbTier = "1";
                e.target.src = `/api/game-assets/petersons/${game.id}.png`;
              } else if (tier === "0" && game.isBooming && game.id) {
                e.target.dataset.fbTier = "1";
                const rawId = String(game.id);
                const _boomingDirect = {
                  "6a59e7c49714218fbf00046a": "/api/game-assets/booming/greedy-for-gold-power-hit-10000-v2.png?v=2",
                };
                const slug = rawId.replace(/^boom_/, "").replace(/_/g, "-");
                e.target.src = _boomingDirect[rawId] || `/api/game-assets/booming/${slug}.png`;
              } else if (tier !== "final") {
                e.target.dataset.fbTier = "final";
                e.target.src = JR_PLACEHOLDER;
              } else {
                e.target.onerror = null;
              }
            }}
          />
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/40">
            <div className="w-10 h-10 rounded-full bg-[#4d7cff] flex items-center justify-center shadow-lg shadow-[#4d7cff]/40 scale-75 group-hover:scale-100 transition-transform duration-300">
              <Play className="w-4 h-4 text-black ml-0.5" fill="black" />
            </div>
          </div>
          {/* Badges */}
          <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
            {game.badge === "HOT" && <span className="px-1.5 py-0.5 bg-red-500/90 text-white text-[7px] font-bold rounded backdrop-blur-sm">HOT</span>}
            {!isNewLaunch(game) && game.badge === "NEW" && <span className="px-1.5 py-0.5 bg-emerald-500/90 text-white text-[7px] font-bold rounded backdrop-blur-sm">NEW</span>}
            {game.jackpot_multiplier === 3 && <span className="px-1.5 py-0.5 bg-[#4d7cff] text-white text-[7px] font-black rounded">3X</span>}
          </div>
          {/* HERO NEW-RELEASE RIBBON — big diagonal corner banner + shine sweep */}
          {isNewLaunch(game) && (
            <>
              <div className="absolute top-0 right-0 z-20 pointer-events-none overflow-hidden w-[100px] h-[100px]">
                <div
                  className="absolute top-[14px] right-[-32px] w-[130px] rotate-45 text-center bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-400 shadow-[0_2px_8px_rgba(0,0,0,0.5)] py-1"
                  data-testid={`new-ribbon-${game.id}`}
                >
                  <span className="text-white text-[10px] font-black tracking-wider drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">
                    ✨ NEW
                  </span>
                </div>
              </div>
              {/* Pulsing glow around card border */}
              <div className="absolute inset-0 pointer-events-none rounded-xl ring-2 ring-emerald-400/60 animate-pulse" />
              {/* Diagonal shine sweep */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute inset-0 opacity-40" style={{
                  background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)",
                  animation: "jr-shine-sweep 2.5s linear infinite",
                }} />
              </div>
            </>
          )}
          {/* Favorite */}
          {onFavorite && (
            <button
              onClick={(e) => { e.stopPropagation(); onFavorite(game.id); }}
              data-testid={`favorite-btn-${game.id}`}
              className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-all z-10 backdrop-blur-sm ${
                isFavorite ? "bg-red-500/80" : "bg-black/40 opacity-0 group-hover:opacity-100"
              }`}
            >
              <Heart className={`w-3 h-3 ${isFavorite ? "fill-white text-white" : "text-white/80"}`} />
            </button>
          )}
        </div>
      </div>
      {/* Game name below the card — centered, clean (Feb 2026: provider
          label removed per UI polish; badges + hover states already
          telegraph the provider). */}
      <p className="text-center text-white text-[11px] font-medium mt-1.5 truncate px-0.5" data-no-sanitize>{game.name}</p>
    </div>
  );
};

// ─── Horizontal Scroll Row (kept for mobile/fallback) ─────
const GameRow = ({ title, icon: Icon, badge, badgeColor, games, onGameClick, onViewAll, onFavorite, favorites = [], accentColor = "amber" }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => { checkScroll(); }, [games]);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });
    setTimeout(checkScroll, 400);
  };

  if (!games || games.length === 0) return null;

  const colors = {
    amber: { text: "text-[#4d7cff]", badgeBg: "bg-[#4d7cff]/15 text-[#4d7cff]" },
    purple: { text: "text-purple-400", badgeBg: "bg-purple-500/15 text-purple-400" },
    emerald: { text: "text-emerald-400", badgeBg: "bg-emerald-500/15 text-emerald-400" },
    cyan: { text: "text-cyan-400", badgeBg: "bg-cyan-500/15 text-cyan-400" },
    rose: { text: "text-rose-400", badgeBg: "bg-rose-500/15 text-rose-400" },
    red: { text: "text-red-400", badgeBg: "bg-red-500/15 text-red-400" },
  };
  const c = colors[accentColor] || colors.amber;

  return (
    <div className="mb-10" data-testid={`game-row-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className={`w-5 h-5 ${c.text}`} />}
          <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
          {badge && (
            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${badgeColor || c.badgeBg}`}>{badge}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-1.5">
            <button onClick={() => scroll(-1)} className={`w-8 h-8 rounded-full border border-white/10 flex items-center justify-center transition-all ${canScrollLeft ? 'text-white hover:bg-white/10' : 'text-zinc-700 cursor-default'}`} disabled={!canScrollLeft}><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scroll(1)} className={`w-8 h-8 rounded-full border border-white/10 flex items-center justify-center transition-all ${canScrollRight ? 'text-white hover:bg-white/10' : 'text-zinc-700 cursor-default'}`} disabled={!canScrollRight}><ChevronRight className="w-4 h-4" /></button>
          </div>
          {onViewAll && (
            <button onClick={onViewAll} className={`${c.text} text-sm font-medium flex items-center gap-0.5 hover:underline`}>
              View All <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div ref={scrollRef} onScroll={checkScroll} className="flex gap-3 md:gap-4 overflow-x-auto pb-2 scrollbar-hide scroll-smooth" style={{ WebkitOverflowScrolling: 'touch' }}>
        {games.slice(0, 20).map((game, idx) => (
          <GameCard key={game.id || idx} game={game} onClick={() => onGameClick(game)} onFavorite={onFavorite} isFavorite={favorites.includes(game.id)} accentColor={accentColor} index={idx} />
        ))}
      </div>
    </div>
  );
};

// ─── Game Card (single tile) ──────────────────────────────
const GameCard = ({ game, onClick, onFavorite, isFavorite, accentColor = "amber", index = 0 }) => {
  // Same authoritative logic as GridGameCard — trust the backend `provider`
  // string first so cross-provider spreads (e.g. Betsoft games that leaked
  // through mascotGames) never mislabel a tile.
  const prov = (game.provider || "").toLowerCase();
  let providerLabel;
  if      (prov.includes("mascot"))   providerLabel = "Mascot";
  else if (prov.includes("ka"))       providerLabel = "KA Gaming";
  else if (prov.includes("booming"))  providerLabel = "Booming";
  else if (prov.includes("betsoft"))  providerLabel = "Betsoft";
  else if (prov.includes("peter"))    providerLabel = "Redwood";
  else if (game.isMascot)             providerLabel = "Mascot";
  else if (game.isKA)                 providerLabel = "KA Gaming";
  else if (game.isBooming)            providerLabel = "Booming";
  else if (game.isBetsoft)            providerLabel = "Betsoft";
  else if (game.isPetersons)          providerLabel = "Redwood";
  else                                providerLabel = game.provider || "JackRoyals";
  
  const borderHover = {
    amber: "hover:border-[#4d7cff]/50 hover:shadow-[0_0_20px_rgba(77,124,255,0.12)]",
    purple: "hover:border-purple-500/50 hover:shadow-[0_0_20px_rgba(139,92,246,0.12)]",
    emerald: "hover:border-emerald-500/50 hover:shadow-[0_0_20px_rgba(16,185,129,0.12)]",
    cyan: "hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(6,182,212,0.12)]",
    rose: "hover:border-rose-500/50 hover:shadow-[0_0_20px_rgba(244,63,94,0.12)]",
    red: "hover:border-red-500/50 hover:shadow-[0_0_20px_rgba(239,68,68,0.12)]",
  };

  return (
    <div
      onClick={onClick}
      data-testid={`game-card-${game.id}`}
      className={`flex-shrink-0 w-[150px] sm:w-[170px] md:w-[190px] group cursor-pointer transition-all duration-300`}
      style={{ animationDelay: `${index * 30}ms` }}
    >
      <div className={`relative rounded-xl overflow-hidden bg-[#0a0f1f] border border-white/[0.06] ${borderHover[accentColor] || borderHover.amber} transition-all duration-300 group-hover:scale-[1.03]`}>
        <div className="aspect-square relative overflow-hidden">
          <img
            src={game.thumbnail || game.image}
            alt={game.name}
            className={`w-full h-full ${game.isKA ? "object-cover" : "object-contain"} transition-transform duration-500 group-hover:scale-105`}
            loading="lazy"
            onError={(e) => {
              // Tiered fallback chain so cards NEVER go blank (which previously
              // looked like flicker on mobile when many images failed at once).
              //   1) Betsoft: try the provider's CDN logo
              //   1) Mascot: try the local 500x500 icon mirror
              //   2) Any provider: swap to a local generic placeholder
              //   3) After tier 2: clear onerror to stop the loop, keep alt text
              const tier = e.target.dataset.fbTier || "0";
              if (tier === "0" && game.isBetsoft && game.gameId) {
                e.target.dataset.fbTier = "1";
                e.target.src = `https://swmd.bfrnd.net/media/games/${game.gameId}/${game.gameId}_logo.png`;
              } else if (tier === "0" && game.isMascot && (game.gameId || game.id)) {
                e.target.dataset.fbTier = "1";
                const gid = game.gameId || game.id;
                e.target.src = `/api/game-assets/mascot/icons_500x500/${gid}_500x500.png`;
              } else if (tier === "0" && game.isPetersons && game.id) {
                e.target.dataset.fbTier = "1";
                e.target.src = `/api/game-assets/petersons/${game.id}.png`;
              } else if (tier === "0" && game.isBooming && game.id) {
                e.target.dataset.fbTier = "1";
                const rawId = String(game.id);
                const _boomingDirect = {
                  "6a59e7c49714218fbf00046a": "/api/game-assets/booming/greedy-for-gold-power-hit-10000-v2.png?v=2",
                };
                const slug = rawId.replace(/^boom_/, "").replace(/_/g, "-");
                e.target.src = _boomingDirect[rawId] || `/api/game-assets/booming/${slug}.png`;
              } else if (tier !== "final") {
                e.target.dataset.fbTier = "final";
                e.target.src = JR_PLACEHOLDER;
              } else {
                e.target.onerror = null;
              }
            }}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/30">
            <div className="w-12 h-12 rounded-full bg-[#4d7cff] flex items-center justify-center shadow-lg shadow-[#4d7cff]/40 scale-75 group-hover:scale-100 transition-transform duration-300">
              <Play className="w-5 h-5 text-black ml-0.5" fill="black" />
            </div>
          </div>

          {/* Provider badge — trust game.provider first, fall back to isX flags */}
          <div className="absolute top-2 left-2">
            {(() => {
              const p = (game.provider || "").toLowerCase();
              if (p.includes("mascot") || (!p && game.isMascot))
                return <span className="px-1.5 py-0.5 bg-purple-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">MASCOT</span>;
              if (p.includes("ka")     || (!p && game.isKA))
                return <span className="px-1.5 py-0.5 bg-emerald-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">KA</span>;
              if (p.includes("booming")|| (!p && game.isBooming))
                return <span className="px-1.5 py-0.5 bg-orange-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">BOOMING</span>;
              if (p.includes("betsoft")|| (!p && game.isBetsoft))
                return <span className="px-1.5 py-0.5 bg-cyan-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">BETSOFT</span>;
              if (p.includes("peter")  || (!p && game.isPetersons))
                return <span className="px-1.5 py-0.5 bg-rose-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">P&S</span>;
              return null;
            })()}
            {game.jackpot_multiplier === 3 && <span className="px-1.5 py-0.5 bg-[#4d7cff] text-white text-[8px] font-black rounded">3X</span>}
            {!game.provider && !game.isMascot && !game.isKA && !game.isBooming && !game.isBetsoft && !game.isPetersons && game.badge === "HOT" && (
              <span className="px-1.5 py-0.5 bg-red-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">HOT</span>
            )}
            {!game.provider && !game.isMascot && !game.isKA && !game.isBooming && !game.isBetsoft && !game.isPetersons && game.badge === "NEW" && game.jackpot_multiplier !== 3 && !isNewLaunch(game) && (
              <span className="px-1.5 py-0.5 bg-emerald-500/90 text-white text-[8px] font-bold rounded backdrop-blur-sm">NEW</span>
            )}
          </div>

          {/* HERO NEW-RELEASE RIBBON — big diagonal corner banner + glow + shine */}
          {isNewLaunch(game) && (
            <>
              <div className="absolute top-0 right-0 z-20 pointer-events-none overflow-hidden w-[120px] h-[120px]">
                <div
                  className="absolute top-[16px] right-[-38px] w-[160px] rotate-45 text-center bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-400 shadow-[0_2px_10px_rgba(0,0,0,0.6)] py-1.5"
                  data-testid={`new-ribbon-lg-${game.id}`}
                >
                  <span className="text-white text-[11px] font-black tracking-widest drop-shadow-[0_1px_1px_rgba(0,0,0,0.7)]">
                    ✨ NEW ✨
                  </span>
                </div>
              </div>
              <div className="absolute inset-0 pointer-events-none rounded-xl ring-2 ring-emerald-400/60 animate-pulse" />
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
                <div className="absolute inset-0 opacity-40" style={{
                  background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)",
                  animation: "jr-shine-sweep 2.5s linear infinite",
                }} />
              </div>
            </>
          )}

          {/* Favorite heart */}
          {onFavorite && (
            <button
              onClick={(e) => { e.stopPropagation(); onFavorite(game.id); }}
              data-testid={`favorite-btn-${game.id}`}
              className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all z-10 backdrop-blur-sm ${
                isFavorite ? "bg-red-500/80" : "bg-black/40 opacity-0 group-hover:opacity-100"
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${isFavorite ? "fill-white text-white" : "text-white/80"}`} />
            </button>
          )}

          {/* Game name below image — centered, provider label removed
              (Feb 2026 clean-tiles polish). */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black via-black/80 to-transparent">
            <p className="text-center text-white text-[11px] font-semibold truncate drop-shadow-lg" data-no-sanitize>{game.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Mobile Game Row ──────────────────────────────────────
const MobileRow = ({ title, games, onGameClick, onViewAll, mascotSlug, mascotTip }) => {
  const [revealedId, setRevealedId] = useState(null);
  if (!games || games.length === 0) return null;

  const handleCardTap = (game) => {
    const gid = game.id || game.gameId || game.name;
    if (revealedId === gid) {
      // Second tap — launch.
      onGameClick(game);
    } else {
      // First tap — reveal title/provider chip.
      setRevealedId(gid);
    }
  };

  const providerLabel = (g) => {
    const p = (g.provider || "").toString();
    if (g.isMascot || /mascot/i.test(p))     return "MASCOT";
    if (g.isKA || /ka gaming|^ka$/i.test(p)) return "KA";
    if (g.isBooming || /booming/i.test(p))   return "BOOMING";
    if (g.isBetsoft || /betsoft/i.test(p))   return "BETSOFT";
    if (g.isPetersons || /peter/i.test(p))   return "P&S";
    if (g.isSpinjoy || /spinjoy/i.test(p))   return "SPINJOY";
    return p.toUpperCase().slice(0, 12);
  };

  return (
    <div className="mb-6" data-testid={`mobile-row-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          {mascotSlug && (
            <Mascot slug={mascotSlug} size="sm" role="inline" tip={mascotTip || `Play ${title} now!`} className="!w-8 !h-8 shrink-0" />
          )}
          <h3 className="text-sm font-bold text-white">{title}</h3>
        </div>
        {onViewAll && (
          <button onClick={onViewAll} className="text-[#4d7cff]/80 text-[11px] font-semibold flex items-center gap-0.5">
            View All <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-hide -mx-3 px-3 snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch' }}>
        {games.slice(0, 20).map((game, idx) => {
          const gid = game.id || game.gameId || game.name;
          const revealed = revealedId === gid;
          return (
          <div
            key={game.id || idx}
            onClick={() => handleCardTap(game)}
            className="flex-shrink-0 w-[110px] cursor-pointer group active:scale-[0.97] transition-transform snap-start"
            data-testid={`mobile-card-${gid}`}
          >
            <div className="relative rounded-xl overflow-hidden aspect-square">
              <img
                src={game.image || game.thumbnail || "/api/game-assets/games/betsoft/_placeholder.jpg"}
                alt={game.name}
                className="w-full h-full object-contain"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const t = e.currentTarget;
                  if (!t.dataset.retried) {
                    t.dataset.retried = "1";
                    t.src = "/api/game-assets/games/betsoft/_placeholder.jpg";
                  } else {
                    t.src = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
                      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 500 500' style='background:#1a1a2e'><rect width='500' height='500' fill='#1a1a2e'/><text x='50%' y='50%' text-anchor='middle' dy='.3em' fill='#666' font-family='sans-serif' font-size='36' font-weight='700'>${(game.name || '').replace(/[<>&]/g, '').slice(0, 20)}</text></svg>`
                    );
                  }
                }}
              />
              {/* Title + provider — hidden by default, revealed on tap.
                  Owner directive Feb 2026: keep the mobile grid clean.
                  First tap → reveal chip, second tap → launch. */}
              <div
                className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-200 pointer-events-none ${revealed ? 'opacity-100' : 'opacity-0'}`}
              />
              <div
                className={`absolute bottom-0 left-0 right-0 p-1.5 transition-all duration-200 pointer-events-none ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
              >
                <p className="text-center text-[10px] font-bold text-white truncate leading-tight" data-no-sanitize>{game.name}</p>
              </div>
              {revealed && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <Play className="w-8 h-8 text-white drop-shadow-lg" fill="white" />
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ───────────────────────────────────────
// ═══════════════════════════════════════════════════════════
const GameLobby = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, fetchUser } = useAuth();
  // Belt-and-suspenders email allowlist — even if the JWT stripping
  // ever drops `is_admin`/`is_tester`, these accounts always see the
  // gated provider rows (Feb 2026 preview-lockdown bypass).
  const _emailLower = (user?.email || "").toLowerCase();
  const _adminEmails = new Set([
    "xdarksleepy1@gmail.com",
    "albro88@yahoo.com",
    "albrim@jackroyals.com",
  ]);
  // Admin gate — real admin/founder flag, used for hiding Spinjoy & 3 Oaks
  // from every non-admin viewer (Feb 2026 owner directive).
  const isAdmin = !!(user && (user.is_admin || user.role === "admin" || user.role === "founder" || _adminEmails.has(_emailLower)));
  // KA gate — admins, founders, and is_ka_tester users only.
  const isKaAdmin = !!(user && (user.is_admin || user.role === "admin" || user.is_ka_tester));
  // Spinjoy is now PUBLIC (Feb 2026 owner directive v2 — unhide for all
  // players). The old admin/tester gate was dropped so Spinjoy games appear
  // in every player's lobby and are searchable by title alongside Mascot,
  // Booming, Betsoft, KA Gaming, Redwood. 3 Oaks remains ADMIN-ONLY until
  // provider IP whitelist is resolved. Spinjoy cert testers
  // (is_tester=true, e.g. spinjoy.tester@jackroyals.com) retain full access
  // identically to regular players — kept as a flag below for future gates.
  const isSpinjoyTester = !!(user && (
    user.is_tester === true ||
    (user.email || "").toLowerCase() === "spinjoy.tester@jackroyals.com"
  ));
  // Exception: 3 Oaks partner reviewers (oaks_test, oaks_stage, oaks_prod)
  // run the staging + production certification flow against prod, so
  // they must see the 3 Oaks row even though it's hidden from regular
  // players.
  const isOaksReviewer = !!(user && [
    "oaks_test@jackroyals.com",
    "oaks_stage@jackroyals.com",
    "oaks_prod@jackroyals.com",
  ].includes((user.email || "").toLowerCase()));
  const canSeeSpinjoy = true;   // Public — Feb 2026 directive v2
  const canSee3Oaks   = isAdmin || isOaksReviewer;
  const PROVIDERS = (() => {
    // PROVIDERS_BASE / PROVIDERS_ADMIN already include "Spinjoy" (Feb 2026 v2).
    const base = isKaAdmin ? PROVIDERS_ADMIN : PROVIDERS_BASE;
    return canSee3Oaks ? [...base, "3 Oaks"] : base;
  })();
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  // When user clicks "View All" / "Show More" on the All Games section, flip this on
  // to force the flat grid (every game) instead of the default sectioned view.
  const [forceFlatView, setForceFlatView] = useState(false);
  const [activeSlotSubcategory, setActiveSlotSubcategory] = useState("all-slots");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("All");
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const [mascotGames, setMascotGames] = useState([]);
  const [boomingGames, setBoomingGames] = useState([]);
  const [betsoftGames, setBetsoftGames] = useState([]);
  const [petersonsGames, setPetersonsGames] = useState([]);
  const [spinjoyGames, setSpinjoyGames] = useState([]);
  const [oaksGames, setOaksGames] = useState([]);
  // Seasonal Section (Feb 2026): "THE JACKPOT GRAVEYARD 🪦" or whatever
  // the admin has renamed it to. Pulled from /api/seasonal-section on
  // mount. Renders as a themed row with purple glow + mist animation
  // and the Wizard mascot next to the header ("Master of the Graveyard").
  const [seasonalSection, setSeasonalSection] = useState(null);
  const [disabledGameIds, setDisabledGameIds] = useState([]);
  const [disabledProviders, setDisabledProviders] = useState([]);
  const [gameOfWeek, setGameOfWeek] = useState(null);
  const [popularGameIds, setPopularGameIds] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  // Stable "Playing Now" counter — drifts slowly (every 9s, ±12) instead of flickering on every render.
  const [playingNow, setPlayingNow] = useState(() => 480 + Math.floor(Math.random() * 180));
  useEffect(() => {
    const id = setInterval(() => {
      setPlayingNow((prev) => {
        const drift = Math.floor(Math.random() * 25) - 12; // -12..+12
        const next = prev + drift;
        // keep in a believable band
        return Math.min(700, Math.max(400, next));
      });
    }, 9000);
    return () => clearInterval(id);
  }, []);

  // Normalize both sides so backend slugs ("ka_gaming") match display names ("KA Gaming").
  // Strip everything non-alphanumeric, lowercase, then substring-match either direction.
  const _normProv = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const isProviderDisabled = (name) => {
    const n = _normProv(name);
    if (!n) return false;
    return disabledProviders.some(dp => {
      const d = _normProv(dp);
      return d && (n.includes(d) || d.includes(n));
    });
  };

  const [jackpots, setJackpots] = useState({ minor: 50, major: 500, mega: 5000 });
  const [userEntries, setUserEntries] = useState(0);
  const [jackpotsLoaded, setJackpotsLoaded] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showWinnerPopup, setShowWinnerPopup] = useState(null);
  const [featuredData, setFeaturedData] = useState(null);
  const [newLaunchIds, setNewLaunchIds] = useState({}); // { provider: Set(gameId) }
  const [newLaunchItems, setNewLaunchItems] = useState([]); // Full items list (newest-first)

  // ─── URL Params ─────────────────────────────────────────
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setTimeout(() => {
        if (window.innerWidth >= 768 && searchInputRef.current) searchInputRef.current.focus();
        else if (mobileSearchInputRef.current) mobileSearchInputRef.current.focus();
      }, 300);
    }
    if (categoryParam) {
      const categoryMap = { 'fish': 'fish', 'shooting': 'shooting', 'slots': 'slots', 'new': 'new', 'classic': 'classic', 'hold-win': 'hold-win', 'all': 'all', 'table': 'table', 'crash': 'crash', 'jackroyals': 'jackroyals' };
      const mappedCategory = categoryMap[categoryParam] || categoryParam;
      if (['new-releases', 'classic', 'hold-win', 'video-slots', 'bonus-buy', 'high-volatility', 'jackpot'].includes(mappedCategory)) {
        setActiveCategory('slots');
        setActiveSlotSubcategory(mappedCategory);
      } else {
        setActiveCategory(mappedCategory);
      }
    }
  }, [searchParams]);

  // ─── Data Fetching ──────────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emergentToken = urlParams.get('emergent_token');
    if (emergentToken) handleGoogleAuthCallback(emergentToken);
    fetchGames(); fetchMascotGames(); fetchBoomingGames(); fetchBetsoftGames(); fetchPetersonsGames();
    // KA fetched separately below once we have an auth token.
    // 3 Oaks + Spinjoy are admin-only (Feb 2026) — fetched in gated effects.
    fetchJackpots(); fetchUserEntries(); loadFavorites(); fetchAnnouncements(); fetchDisabledGames();
    fetchGameOfWeek(); fetchPopularGames(); fetchFeaturedGames(); fetchNewLaunches(); fetchBetsoftJulyPromo();
    fetchSeasonalSection();
  }, []);

  // ─── Seasonal Section ("Jackpot Graveyard" — Feb 2026) ──────
  // Public endpoint returns {enabled, name, subtitle, mascot_slug,
  // mascot_tip, theme, games[]}. We only render the row if it's
  // enabled and has games. Backend-driven so the admin can retheme
  // (Halloween → Christmas → Valentines) without a frontend deploy.
  const fetchSeasonalSection = async () => {
    try {
      const res = await axios.get(`${API}/seasonal-section`);
      if (res.data && res.data.enabled && (res.data.games || []).length > 0) {
        setSeasonalSection(res.data);
      } else {
        setSeasonalSection(null);
      }
    } catch (_e) {
      setSeasonalSection(null);
    }
  };

  // KA Gaming is publicly launched (Aug 2026). Fetch for every logged-in
  // user — the backend gate returns {games:[]} for anyone not authorized
  // if closed-testing ever comes back, so this is safe. Previously this
  // was gated to `isKaAdmin` which is why regular players on mobile saw
  // an empty KA row.
  useEffect(() => {
    if (token) fetchKAGames();
    else setKaGames([]);
  }, [token]);

  // Spinjoy — PUBLIC (Feb 2026 directive v2). Fetched for every viewer so
  // the Spinjoy filter tab + title-search surface the full catalog in the
  // lobby. Backend `/spinjoy/games` is also unlocked (no admin/tester
  // gate) to match.
  useEffect(() => {
    fetchSpinjoyGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 3 Oaks — ADMIN-ONLY (Feb 2026). Same gating pattern as Spinjoy.
  useEffect(() => {
    if (canSee3Oaks) fetchOaksGames();
    else setOaksGames([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee3Oaks, token]);

  // Re-fetch the disabled-providers list whenever the auth token changes
  // so admin/tester bypass records (Spinjoy for is_tester, 3 Oaks for
  // oaks_test) take effect immediately after login — otherwise the mount
  // fetch runs with an anon header and the row stays filtered out even
  // after a fresh sign-in.
  useEffect(() => {
    if (token) fetchDisabledGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Betsoft July 2026 30% promo — surfaces a curated TOP Games section.
  const [julyPromo, setJulyPromo] = useState(null);
  const fetchBetsoftJulyPromo = async () => {
    try {
      const res = await axios.get(`${API}/betsoft/july-promo`);
      setJulyPromo(res.data);
    } catch (e) { /* non-fatal */ }
  };

  const fetchNewLaunches = async () => {
    try {
      const res = await axios.get(`${API}/new-game-launches`);
      // Convert ids_by_provider arrays into Sets for O(1) membership checks
      const map = {};
      const src = res.data?.ids_by_provider || {};
      Object.entries(src).forEach(([provider, ids]) => {
        map[provider] = new Set((ids || []).map(String));
      });
      setNewLaunchIds(map);
      // Full items list (name, provider, launch_date) — powers the NEW GAMES lobby section
      const items = res.data?.items || [];
      setNewLaunchItems(items);
      // Build launch-date lookup { provider: { game_id: ms_timestamp } } so
      // the New/Top/Featured sorts can order titles newest-first.
      const dateMap = {};
      items.forEach((it) => {
        const p = it.provider;
        if (!p) return;
        if (!dateMap[p]) dateMap[p] = {};
        dateMap[p][String(it.game_id)] = Date.parse(it.launch_date) || 0;
      });
      // Expose globally so deeply-nested GameCard renders can check without prop-drilling
      if (typeof window !== "undefined") {
        window.__jrNewLaunches = map;
        window.__jrNewLaunchDates = dateMap;
      }
    } catch (e) { console.error("Failed to fetch new launches:", e); }
  };

  const fetchFeaturedGames = async () => {
    try {
      const res = await axios.get(`${API}/featured-games`);
      setFeaturedData(res.data);
    } catch (e) { console.error("Failed to fetch featured games:", e); }
  };

  const fetchDisabledGames = async () => {
    try {
      // Send auth token (if present) so admins automatically bypass admin_only-disabled providers
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/disabled-games-list`, { headers });
      setDisabledGameIds(res.data.disabled_game_ids || []);
      setDisabledProviders(res.data.disabled_providers || []);
    } catch (e) { console.error("Failed to fetch disabled games:", e); }
  };

  const fetchGameOfWeek = async () => {
    try { const res = await axios.get(`${API}/features/game-of-week`); if (res.data.game) setGameOfWeek(res.data.game); } catch (e) { console.error(e); }
  };

  const fetchPopularGames = async () => {
    try { const res = await axios.get(`${API}/features/popular-games?limit=30`); setPopularGameIds((res.data.games || []).map(g => g.game_id)); } catch (e) { console.error(e); }
  };

  const redeemPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/features/promo/redeem`, { code: promoCode }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setPromoCode("");
      if (fetchUser) fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid promo code");
    } finally { setPromoLoading(false); }
  };

  const handleGoogleAuthCallback = async (emergentToken) => {
    try {
      const res = await axios.post(`${API}/auth/google-callback`, { emergent_token: emergentToken });
      sessionStorage.setItem('token', res.data.access_token);
      window.history.replaceState({}, document.title, window.location.pathname);
      await fetchUser();
      toast.success("Welcome to JackRoyals!");
    } catch (error) {
      console.error("Google auth callback failed:", error);
      toast.error(error.response?.data?.detail || "Google login failed");
      navigate('/');
    }
  };

  // Jackpot ticker now lives inside <TickingJackpot/> so it does NOT re-render
  // the whole GameLobby every second (which was causing visible flicker on
  // mobile and rebuilding the 1000+ game array on every tick).

  const fetchJackpots = async () => {
    try {
      const res = await axios.get(`${API}/jackpot`);
      const data = { minor: res.data.minor || 50, major: res.data.major || 500, mega: res.data.mega || 5000 };
      setJackpots(data);
      setJackpotsLoaded(true);
    } catch (e) { console.error(e); }
  };

  const fetchUserEntries = async () => {
    if (!token) return;
    try { const res = await axios.get(`${API}/jackpot/entries`, { headers: { Authorization: `Bearer ${token}` } }); setUserEntries(res.data.entries || 0); } catch (e) { console.error(e); }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/jackpot-announcements`);
      if (res.data && res.data.length > 0) {
        setAnnouncements(res.data);
        const lastShownId = localStorage.getItem("lastAnnouncementId");
        const latest = res.data[0];
        if (latest && latest.id !== lastShownId) { setShowWinnerPopup(latest); localStorage.setItem("lastAnnouncementId", latest.id); }
      }
    } catch (e) { console.error(e); }
  };

  const fetchGames = async () => { try { const res = await axios.get(`${API}/games`); setGames(res.data); setLoading(false); } catch (e) { console.error(e); setLoading(false); } };

  const fetchMascotGames = async () => {
    try {
      const res = await axios.get(`${API}/mascot/games`);
      setMascotGames((res.data.games || []).map(game => ({ ...game, provider: "Mascot", isMascot: true, isFeatured: true })));
    } catch (e) { setMascotGames([]); }
  };

  // Spinjoy — public catalog (Feb 2026 v2). Appended to the master
  // games pool so provider filter + title search work alongside other
  // providers.
  const fetchSpinjoyGames = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/spinjoy/games`, { headers });
      const list = (res.data.games || []).map(g => ({
        ...g,
        id:        g.code,
        game_id:   g.code,
        gameId:    g.code,
        name:      g.name,
        provider:  "Spinjoy",
        isSpinjoy: true,
        isFeatured: true,
        // Prefer the hi-res square (~500×500) over thumbnail (~150×150)
        // so lobby tiles render crisp. For games with no provider art
        // (e.g. creamy-bonanza), the Featured/TOP pin thumbnails fall
        // back to self-hosted /api/game-assets/spinjoy/*.png.
        image:     g.images?.square || g.images?.horizontal || g.thumbnail || g.images?.thumbnail || "",
        thumbnail: g.images?.square || g.images?.horizontal || g.thumbnail || g.images?.thumbnail || "",
      }));
      setSpinjoyGames(list);
    } catch (e) { setSpinjoyGames([]); }
  };

  // KA Gaming — gated to admins / founders / KA_TESTER_EMAILS during closed testing.
  // Backend returns {games: [], access: "closed_testing"} for non-testers so the
  // lobby gracefully shows nothing instead of breaking. When KA_PUBLIC_ACCESS=true
  // on the backend, all logged-in users see the full 988-game catalog.
  const [kaGames, setKaGames] = useState([]);
  const fetchKAGames = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/ka/games`, { headers });
      setKaGames((res.data.games || []).map(g => {
        const gid = g.gameId || g.id;
        // OVERRIDE: our own tight square covers win over the KA CDN wide art.
        const override = KA_THUMB_OVERRIDES[gid] || KA_THUMB_OVERRIDES[g.gameName];
        // Always route through OUR proxy `/api/ka/thumb/{gid}` so the
        // browser sees a same-origin image. Direct KA-CDN URLs are dropped
        // by Chrome's Opaque Response Blocking (ERR_BLOCKED_BY_RESPONSE.
        // NotSameOrigin) because their CDN doesn't set the
        // Cross-Origin-Resource-Policy header — proxying through us adds it.
        const thumb = override || `${API}/ka/thumb/${gid}`;
        return {
          ...g,
          id:        gid,
          game_id:   gid,
          name:      g.gameName || g.name,
          provider:  "KA Gaming",
          isKA:      true,
          isFeatured: true,
          image:     override || thumb,
          thumbnail: override || thumb,
        };
      }));
    } catch (e) { setKaGames([]); }
  };

  const fetchBoomingGames = async () => {
    try { const res = await axios.get(`${API}/booming/games`); setBoomingGames(res.data.games || []); } catch (e) { console.error(e); }
  };

  const fetchOaksGames = async () => {
    // 3 Oaks Gaming — Admin-only visibility (Feb 2026). Skip fetch entirely
    // for non-admin users so the network tab stays clean and no tiles leak
    // through a UI state race. Backend also enforces the gate.
    if (!canSee3Oaks) { setOaksGames([]); return; }
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/oaks/games`, { headers });
      setOaksGames(res.data.games || []);
    } catch (e) { console.error(e); }
  };

  const fetchBetsoftGames = async () => {
    try {
      const res = await axios.get(`${API}/betsoft/games`);
      const allGames = [];
      if (res.data.gamesSuites) {
        res.data.gamesSuites.forEach(suite => {
          if (suite.games) {
            suite.games.forEach(game => {
              // ────────────────────────────────────────────────────────
              // THUMBNAIL RESOLUTION PRIORITY (fixed 2026-08-06):
              // 1. Backend-provided imageUrl if it's a local /games/betsoft/*
              //    path — this is the SOURCE OF TRUTH because the backend
              //    ships BETSOFT_THUMBNAIL_OVERRIDES for games whose Betsoft
              //    CDN thumbnails are broken or missing.
              // 2. Backend-provided imageUrl for pre-release games (fresh
              //    Betsoft CDN thumbnails not yet in our local mapping).
              // 3. Local getBetsoftGameImage() mapping.
              //
              // Previously we only used the backend URL for pre-release games,
              // which meant once a game hit Betsoft's live catalog (like
              // The Governor 1015, Last Laugh 1020 etc.), the frontend
              // silently fell back to a stale local thumb — producing the
              // "same slot machine image on every new Betsoft tile" bug.
              // ────────────────────────────────────────────────────────
              const localImage = getBetsoftGameImage(game.gameId, game.gameName);
              const backendImg = typeof game.imageUrl === "string" ? game.imageUrl : "";
              // Trust the backend override URL for BOTH the legacy /games/betsoft/*
              // path AND the newer /api/game-assets/games/betsoft/* path (K8s
              // ingress-compat mount, used since 2026-08). This way any new
              // Betsoft title only needs the backend BETSOFT_THUMBNAIL_OVERRIDES
              // entry + the file on disk — the frontend picks it up automatically
              // without a data/betsoftGameImages.js code push.
              const isBackendBetsoftOverride =
                backendImg.startsWith("/games/betsoft/") ||
                backendImg.startsWith("/api/game-assets/games/betsoft/");
              let imageUrl;
              if (isBackendBetsoftOverride) {
                imageUrl = backendImg;                          // hard override — win
              } else if (game.isPreRelease && backendImg) {
                imageUrl = backendImg;                          // pre-release CDN
              } else {
                imageUrl = localImage;                          // legacy mapping
              }
              const displayName = GAME_NAME_OVERRIDES[String(game.gameId)] || game.gameName;
              allGames.push({
                id: `betsoft_${game.gameId}`,
                name: displayName,
                image: imageUrl,
                thumbnail: imageUrl,
                provider: "Betsoft",
                category: suite.suiteName.toLowerCase().includes("slot") ? "slots" : suite.suiteName.toLowerCase().includes("table") ? "table" : suite.suiteName.toLowerCase().includes("poker") ? "table" : "slots",
                rtp: 96.0,
                gameId: game.gameId,
                suiteName: suite.suiteName,
                isPreRelease: !!game.isPreRelease,
                badge: game.isPreRelease ? "NEW" : undefined,
              });
            });
          }
        });
      }
      // ═══════════════════════════════════════════════════════════════════
      //  BETSOFT ORDERING: newest HD titles ON TOP.
      //  Betsoft's numeric gameIds are assigned chronologically, so the
      //  highest IDs are always their newest HD releases (Last Laugh 1020,
      //  The Robbery 1016, The Governor 1015, Plinko Cup 997, Diamond Mines
      //  959, Olympus Plinko 940, Plinko Rush 915, Supreme 777 872, Triple
      //  Cash or Crash 863 …). Sorting DESC surfaces the premium recent
      //  releases first and pushes the legacy table games (Baccarat 13,
      //  Craps 10, Pai Gow 9, American Roulette 5, Lucky Seven 2) to the
      //  bottom where they belong.
      allGames.sort((a, b) => {
        // Pre-release / injected NEW games always first
        if (a.isPreRelease && !b.isPreRelease) return -1;
        if (!a.isPreRelease && b.isPreRelease) return 1;
        // Then by numeric gameId DESC (newer = higher)
        const na = parseInt(a.gameId, 10) || 0;
        const nb = parseInt(b.gameId, 10) || 0;
        return nb - na;
      });
      setBetsoftGames(allGames);
    } catch (e) { console.error(e); }
  };

  const fetchPetersonsGames = async () => {
    try { const res = await axios.get(`${API}/petersons/games`); setPetersonsGames((res.data.games || []).map(g => ({ ...g, provider: "Redwood", isPetersons: true, image: g.thumbnail }))); } catch (e) { console.error(e); }
  };

  // ─── Game Actions ───────────────────────────────────────
  const loadFavorites = () => { const saved = localStorage.getItem("favorites"); if (saved) setFavorites(JSON.parse(saved)); };

  const toggleFavorite = (gameId) => {
    const newFavorites = favorites.includes(gameId) ? favorites.filter(id => id !== gameId) : [...favorites, gameId];
    setFavorites(newFavorites);
    localStorage.setItem("favorites", JSON.stringify(newFavorites));
    if (newFavorites.includes(gameId)) toast.success("Added to favorites!"); else toast("Removed from favorites");
  };

  const playGame = (game) => {
    const gid = game.gameId || game.id;
    if (gid) axios.post(`${API}/features/track-play/${gid}`).catch(() => {});
    const provider = (game.provider || "").toLowerCase();
    if (game.isMascot || provider === "mascot") { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/mascot/${game.gameId || game.id}`, { state: { game, coinType: "sweeps" } }); return; }
    if (game.isKA || provider === "ka") { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/ka/${game.gameId || game.id}`, { state: { game, coinType: "sweeps" } }); return; }
    if (game.isBooming || provider.includes("booming")) { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/booming/${game.id}`); return; }
    if (game.isBetsoft || provider === "betsoft") { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/betsoft/${game.gameId || game.id}`); return; }
    if (game.isPetersons || provider.includes("peter")) { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/petersons/${game.id}`); return; }
    if (game.isSpinjoy || provider === "spinjoy") { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/spinjoy/${game.code || game.gameId || game.id}`); return; }
    if (game.isOaks || provider === "3 oaks" || provider === "3oaks") { if (!token) { toast.error("Please login to play"); return; } navigate(`/game/oaks/${game.gameId || game.id}`); return; }
    if (game.id?.startsWith("jr-") || game.game_url) { navigate(`/game/external/${game.id}`); return; }
    if (game.category === "blackjack" || game.id === "blackjack" || game.id === "table-1") { navigate("/game/blackjack"); }
    else if (game.id === "table-2" || game.name?.toLowerCase().includes("roulette")) { navigate("/game/roulette"); }
    else if (game.id === "table-4" || game.name?.toLowerCase().includes("poker") || game.name?.toLowerCase().includes("hold'em")) { navigate("/game/poker"); }
    else if (game.id === "crash-1" || game.category === "crash") { navigate("/game/crash"); }
    else if (game.category === "table") { toast("This table game is coming soon!"); }
    else { toast("Games coming soon from premium providers!"); }
  };

  // ─── Filtering ──────────────────────────────────────────
  useEffect(() => { filterGames(); }, [games, activeCategory, activeSlotSubcategory, searchQuery, favorites, selectedProvider, mascotGames, boomingGames, betsoftGames, petersonsGames, spinjoyGames, oaksGames, canSeeSpinjoy, canSee3Oaks, disabledGameIds, popularGameIds, featuredData]);

  const filterBySlotSubcategory = (games) => {
    if (activeSlotSubcategory === "all-slots") return games;
    return games.filter(game => {
      const name = (game.name || "").toLowerCase();
      const tags = (game.tags || []).map(t => t.toLowerCase());
      const volatility = (game.volatility || "").toLowerCase();
      switch (activeSlotSubcategory) {
        case "hold-win": {
          const gCat = classifyGame(game);
          if (gCat === "table" || gCat === "fish" || gCat === "crash") return false;
          return name.includes("hold and win") || name.includes("hold & win") || name.includes("hold n win") || name.includes("hold the spin") || name.includes("hold the gold") || name.includes("hold n link") || name.includes("cash collect") || name.includes("link & win") || name.includes("link and win") || name.includes("lock and spin") || name.includes("lock & spin") || name.includes("lock it link") || name.includes("respin") || name.includes("re-spin") || name.includes("stacks") || name.includes("coins") || tags.includes("hold and win") || tags.includes("hold & win") || tags.includes("respin");
        }
        case "jackpot": return name.includes("jackpot") || name.includes("progressive") || name.includes("mega") || name.includes("grand") || name.includes("major") || name.includes("fortune") || name.includes("million") || name.includes("prize") || name.includes("pot") || game.isJackpot || tags.includes("jackpot");
        case "classic": return name.includes("classic") || name.includes("777") || name.includes("fruit") || name.includes("bar") || name.includes("cherry") || name.includes("retro") || name.includes("7s") || name.includes("sevens") || name.includes("bells") || name.includes("diamond") || name.includes("lucky") || name.includes("hot") || name.includes("joker") || name.includes("fire") || tags.includes("classic") || tags.includes("fruit");
        case "video-slots": return name.includes("book") || name.includes("quest") || name.includes("king") || name.includes("queen") || name.includes("zeus") || name.includes("god") || name.includes("dragon") || name.includes("wolf") || name.includes("egypt") || name.includes("aztec") || name.includes("viking") || name.includes("pirate") || name.includes("magic") || name.includes("wizard") || name.includes("princess") || name.includes("knight") || name.includes("empire") || name.includes("legend") || name.includes("myth") || name.includes("ancient") || name.includes("temple") || name.includes("tomb") || name.includes("safari") || name.includes("jungle") || name.includes("ocean") || name.includes("space");
        case "bonus-buy": return name.includes("bonus") || name.includes("buy") || name.includes("feature") || name.includes("free") || name.includes("spin") || name.includes("round") || name.includes("pick") || name.includes("wheel") || name.includes("multiplier") || name.includes("expand") || tags.includes("bonus buy") || tags.includes("feature buy");
        case "high-volatility": return volatility === "high" || name.includes("max") || name.includes("extreme") || name.includes("mega") || name.includes("ultra") || name.includes("super") || name.includes("big") || name.includes("huge") || name.includes("massive") || name.includes("giant") || name.includes("thunder") || name.includes("storm") || name.includes("power") || name.includes("rage") || name.includes("fury") || name.includes("wild") || name.includes("x") || (game.maxWin && game.maxWin >= 3000) || tags.includes("high volatility");
        case "new-releases": return game.isNew || game.isFeatured || (game.releaseDate && (new Date() - new Date(game.releaseDate)) < 90 * 24 * 60 * 60 * 1000) || tags.includes("new") || name.includes("new") || name.includes("deluxe") || name.includes("2") || name.includes("ii") || name.includes("reloaded");
        default: return true;
      }
    });
  };

  // Convert a curated featured/top entry (from /featured-games) into the
  // shape the GameLobby cards expect, preserving provider flags so cards render correctly.
  const curatedToGame = (c) => {
    const prov = (c.provider || "").toLowerCase();
    const isMascot = prov === "mascot";
    const isBooming = prov.includes("booming");
    const isBetsoft = prov === "betsoft";
    const isPetersons = prov.includes("peter");
    const thumb = resolveCuratedThumb(c);
    return {
      id: c.game_id,
      gameId: c.game_id,
      name: c.name,
      provider: c.provider,
      thumbnail: thumb,
      image: thumb,
      badge: c.badge || null,
      isMascot,
      isBooming,
      isBetsoft,
      isPetersons,
      category: classifyGame({ name: c.name, category: "" }),
    };
  };

  const filterGames = () => {
    let filtered = [...games];
    const mascotFormatted   = mascotGames.map(g   => ({ ...g, provider: "Mascot",       category: classifyGame(g), isMascot: true,   isFeatured: true }));
    const kaFormatted       = kaGames.map(g       => ({ ...g, provider: "KA Gaming",    category: classifyGame(g), isKA: true,       isFeatured: true }));
    const boomingFormatted  = boomingGames.map(g  => ({ ...g, provider: "Booming",      category: classifyGame(g), isBooming: true }));
    const betsoftFormatted  = betsoftGames.map(g  => ({ ...g, provider: "Betsoft",      category: classifyGame(g), isBetsoft: true }));
    const petersonsFormatted= petersonsGames.map(g=> ({ ...g, provider: "Redwood", category: classifyGame(g), isPetersons: true }));
    // Spinjoy — public (Feb 2026 v2). Always included in the merged pool so
    // the Spinjoy provider filter + title-search surface the full catalog.
    const spinjoyFormatted  = spinjoyGames.map(g => ({ ...g, provider: "Spinjoy", category: classifyGame(g), isSpinjoy: true, isFeatured: true }));
    const oaksFormatted     = canSee3Oaks
      ? oaksGames.map(g => ({ ...g, provider: "3 Oaks", category: classifyGame(g), isOaks: true, isPreRelease: !!g.is_pre_release }))
      : [];
    filtered = [...mascotFormatted, ...kaFormatted, ...filtered, ...boomingFormatted, ...betsoftFormatted, ...petersonsFormatted, ...spinjoyFormatted, ...oaksFormatted];
    // Assign badges
    filtered = filtered.map(g => {
      const gid = String(g.gameId || g.id);
      let badge = g.badge || null;
      if (!badge && popularGameIds.includes(gid)) badge = "HOT";
      if (!badge && (g.isNew || g.isFeatured || (g.releaseDate && (new Date() - new Date(g.releaseDate)) < 90 * 24 * 60 * 60 * 1000))) badge = "NEW";
      return { ...g, badge };
    });
    if (searchQuery) { const q = searchQuery.toLowerCase(); filtered = filtered.filter(g => g.name?.toLowerCase().includes(q) || g.provider?.toLowerCase().includes(q)); }
    if (selectedProvider !== "All") {
      if (selectedProvider === "Mascot") filtered = filtered.filter(g => g.isMascot || g.provider === "Mascot");
      if (selectedProvider === "KA Gaming" || selectedProvider === "KA") filtered = filtered.filter(g => g.isKA || g.provider === "KA Gaming");
      else if (selectedProvider === "Booming Games") filtered = filtered.filter(g => g.isBooming || g.provider === "Booming Games");
      else if (selectedProvider === "Betsoft") filtered = filtered.filter(g => g.isBetsoft || g.provider === "Betsoft");
      else if (selectedProvider === "Redwood") filtered = filtered.filter(g => g.isPetersons || g.provider === "Redwood");
      else if (selectedProvider === "Spinjoy") filtered = filtered.filter(g => g.isSpinjoy || g.provider === "Spinjoy");
      else filtered = filtered.filter(g => g.provider?.toLowerCase().includes(selectedProvider.toLowerCase()));
    }
    switch (activeCategory) {
      case "featured": {
        const curated = (featuredData?.featured || []).map(curatedToGame);
        // Honor provider filter on curated list too
        filtered = selectedProvider !== "All"
          ? curated.filter(g => (g.provider || "").toLowerCase().includes(selectedProvider.toLowerCase()))
          : curated;
        break;
      }
      case "top": {
        const curated = (featuredData?.top || []).map(curatedToGame);
        filtered = selectedProvider !== "All"
          ? curated.filter(g => (g.provider || "").toLowerCase().includes(selectedProvider.toLowerCase()))
          : curated;
        break;
      }
      case "slots": filtered = filtered.filter(g => g.category === "slots"); filtered = filterBySlotSubcategory(filtered); break;
      case "new":
        {
          // ═══════════════════════════════════════════════════════════
          // NEW GAMES = only games we actually just added (last ~30
          // days), NOT the entire slice of a provider's catalog.
          // We identify "just added" via:
          //   1. `is_new: true` flag on the game (backend / catalog set)
          //   2. `isPreRelease: true` (Betsoft newly-launched titles)
          //   3. `release_date` within the last 30 days
          //   4. Explicit ID whitelist for pre-release games not yet
          //      surfaced in provider catalogs (safety net).
          const RECENTLY_ADDED_IDS = new Set([
            "1020", "betsoft_1020",                                    // Last Laugh
            "1015", "betsoft_1015",                                    // The Governor
            "1016", "betsoft_1016",                                    // The Robbery
            "69442ddbd7534021c400000d",                                // Henhouse Hustle: Hold and Win
            "6972391e69fdac758e00001f",                                // Bubble Works
            "69b404993c7f931e4f000027",                                // Tasty Bonanza Max Scatter
            "696a52ef69fdac3416000011",                                // Fish Tales: Double Catch Max Scatter
            "boom_henhouse_hustle_hold_and_win",
            "boom_bubble_works",
            "boom_tasty_bonanza_max_scatter",
            "boom_fish_tales_double_catch_max_scatter",
          ]);
          const cutoffMs = Date.now() - 30 * 24 * 60 * 60 * 1000;
          const isRecentRelease = (g) => {
            const dt = g.release_date || g.releaseDate;
            if (!dt) return false;
            const t = new Date(dt).getTime();
            return !Number.isNaN(t) && t >= cutoffMs;
          };
          // Include KA in the merge so freshly-seeded KA titles surface here.
          // 2026-08-19 fix — Nainer / KA batch was missing from this pool.
          const allG = [...mascotFormatted, ...filtered, ...boomingFormatted, ...betsoftFormatted, ...petersonsFormatted, ...kaFormatted];
          filtered = allG.filter(g => {
            const gid = String(g.gameId || g.id);
            const bid = String(g.booming_id || "");
            return (
              // Backend-authoritative check — anything registered in
              // /api/new-game-launches (14-day window) wins immediately.
              isNewLaunch(g) ||
              RECENTLY_ADDED_IDS.has(gid) ||
              RECENTLY_ADDED_IDS.has(bid) ||
              g.is_new === true ||
              g.isPreRelease === true ||
              isRecentRelease(g)
            );
          });
          // De-dupe by id, keep the first occurrence
          const seenIds = new Set();
          filtered = filtered.filter(g => {
            const key = String(g.gameId || g.id);
            if (seenIds.has(key)) return false;
            seenIds.add(key);
            return true;
          });
        }
        break;
      case "fish": filtered = filtered.filter(g => g.category === "fish"); break;
      case "shooting": filtered = filtered.filter(g => g.category === "shooting"); break;
      case "jackroyals": filtered = filtered.filter(g => JACKROYALS_PROVIDERS.includes(g.provider)); break;
      case "crash": filtered = filtered.filter(g => g.category === "crash"); break;
      case "table": filtered = filtered.filter(g => g.category === "table" || g.category === "blackjack" || g.category === "poker" || g.category === "roulette" || g.category === "baccarat"); break;
      case "favorites": filtered = filtered.filter(g => favorites.includes(g.id)); break;
      case "popular": {
        // ═══════════════════════════════════════════════════════════
        // POPULAR = top 20 total, capped at 5 per provider so no single
        // provider floods the section. The `popularGameIds` list from
        // /features/popular-games preserves quality ranking (most-played
        // first); we walk that list and pick the first 5 of each
        // provider until we hit 20.
        const popIndex = new Map();
        popularGameIds.forEach((gid, i) => popIndex.set(String(gid), i));
        const pop = filtered
          .filter(g => popIndex.has(String(g.gameId || g.id)))
          .sort((a, b) => (popIndex.get(String(a.gameId || a.id)) - popIndex.get(String(b.gameId || b.id))));
        const perProviderCap = 5;
        const totalCap = 20;
        const perProviderCount = {};
        const balanced = [];
        for (const g of pop) {
          if (balanced.length >= totalCap) break;
          const p = g.provider || "Other";
          const cnt = perProviderCount[p] || 0;
          if (cnt >= perProviderCap) continue;
          perProviderCount[p] = cnt + 1;
          balanced.push(g);
        }
        filtered = balanced;
        break;
      }
      default: break;
    }
    if (activeCategory === "slots" && activeSlotSubcategory !== "all-slots") filtered = shuffleArray(filtered);
    if (disabledGameIds.length > 0 || disabledProviders.length > 0) {
      filtered = filtered.filter(g => {
        if (disabledGameIds.includes(g.id) || disabledGameIds.includes(String(g.id))) return false;
        if (isProviderDisabled(g.provider)) return false;
        return true;
      });
    }
    setFilteredGames(filtered);
  };

  // Helper: get all games combined (with provider flags) — filtered by admin's disabled list.
  // MEMOIZED so we don't rebuild a 1000+ item array on every parent re-render
  // (was causing mobile flicker before the jackpot ticker was isolated).
  //
  // Spinjoy is included so title-search + the "Spinjoy" provider filter
  // tab resolve against the full catalog alongside the other providers.
  // 3 Oaks stays out of the master pool because it's still admin-only.
  const allCombinedRaw = useMemo(() => [
    ...mascotGames.map(g    => ({ ...g, provider: "Mascot",       isMascot: true,    category: classifyGame(g) })),
    ...kaGames.map(g        => ({ ...g, provider: "KA Gaming",    isKA: true,        category: classifyGame(g) })),
    ...games,
    ...boomingGames.map(g   => ({ ...g, provider: "Booming",      isBooming: true,   category: classifyGame(g) })),
    ...betsoftGames.map(g   => ({ ...g, provider: "Betsoft",      isBetsoft: true,   category: classifyGame(g) })),
    ...petersonsGames.map(g => ({ ...g, provider: "Redwood", isPetersons: true, category: classifyGame(g) })),
    ...spinjoyGames.map(g   => ({ ...g, provider: "Spinjoy",      isSpinjoy: true,   category: classifyGame(g) })),
  ], [mascotGames, kaGames, games, boomingGames, betsoftGames, petersonsGames, spinjoyGames]);
  // Filter out admin-disabled games + disabled providers
  const allCombined = useMemo(() => allCombinedRaw.filter(g => {
    const gid = String(g.id ?? g.gameId ?? "");
    if (disabledGameIds.includes(gid) || disabledGameIds.includes(g.id) || disabledGameIds.includes(g.gameId)) return false;
    const providerName = (g.isMascot ? "Mascot" : g.isBooming ? "Booming Games" : g.isBetsoft ? "Betsoft" : g.isPetersons ? "Redwood" : g.isSpinjoy ? "Spinjoy" : g.isKA ? "KA Gaming" : (g.provider || "JackRoyals"));
    if (isProviderDisabled(providerName)) return false;
    return true;
  }), [allCombinedRaw, disabledGameIds, disabledProviders]);

  const isDefaultView = activeCategory === "all" && !searchQuery && selectedProvider === "All" && !forceFlatView;

  // ═══════════════════════════════════════════════════════════
  // ─── RENDER ─────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════
  return (
    <MainLayout>
      <div className="min-h-screen relative z-[1]">

        {/* Holiday Top Banner — Labor Day upgrade sitewide ribbon
            (Feb 2026: replaces the previous NEW-RELEASE ribbon). Uses the
            brand navy/gold gradient so it never fights with the hero card
            below it, and includes a subtle shimmer sweep. */}
        <div
          className="relative w-full overflow-hidden border-y border-amber-500/25"
          style={{
            background: "linear-gradient(90deg, #12162d 0%, #1a1040 25%, #2a1a04 50%, #1a1040 75%, #12162d 100%)",
          }}
          data-testid="lobby-holiday-banner"
        >
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(251,191,36,0.10) 50%, transparent 100%)",
              animation: "lobbyBannerShimmer 4s ease-in-out infinite",
            }}
          />
          <div className="relative flex items-center justify-center px-4 py-2 sm:py-2.5">
            <span
              className="text-[11px] sm:text-sm font-black tracking-wide text-center"
              style={{
                color: "#fef3c7",
                textShadow: "0 0 10px rgba(251,191,36,0.35)",
              }}
              data-testid="lobby-holiday-banner-text"
            >
              🔱 THE LABOR DAY UPGRADE:{" "}
              <span style={{ color: "#fde68a" }}>Instant Payouts.</span>{" "}
              <span style={{ color: "#fde68a" }}>2x Playthrough.</span>{" "}
              <span style={{ color: "#fde68a" }}>No Blocks.</span>{" "}
              🎰
            </span>
          </div>
          <style>{`
            @keyframes lobbyBannerShimmer {
              0%, 100% { transform: translateX(-100%); }
              50%      { transform: translateX(100%); }
            }
          `}</style>
        </div>

        {/* ══════════ MOBILE LAYOUT ══════════ */}
        <div className="md:hidden px-3 pt-3 pb-24">
          {/* Mobile Reward Strip */}
          <div className="flex gap-1.5 mb-3">
            {[
              { label: "MINOR", base: jackpots.minor, step: 0.03, color: '#00ff88', border: 'rgba(0,255,136,0.2)' },
              { label: "GRAND", base: jackpots.mega,  step: 0.15, color: '#4d7cff', border: 'rgba(77,124,255,0.3)', highlight: true },
              { label: "MAJOR", base: jackpots.major, step: 0.08, color: '#00d4ff', border: 'rgba(0,212,255,0.2)' },
            ].map(j => (
              <div key={j.label} onClick={() => navigate("/jackpots")} className="flex-1 py-2 px-1.5 rounded-lg bg-black/40 text-center cursor-pointer" style={{ border: `1px solid ${j.border}`, background: j.highlight ? 'rgba(77,124,255,0.05)' : 'rgba(0,0,0,0.4)' }}>
                <p className="text-[8px] font-bold tracking-widest" style={{ color: j.color }}>{j.label}</p>
                <p className="text-white font-bold text-[11px] tabular-nums"><TickingJackpot base={j.base} step={j.step} /> SC</p>
              </div>
            ))}
          </div>

          {/* Giveaway Banner - Mobile */}
          <div className="mb-4">
            <GiveawayLobbyBanner />
            <WelcomeBonusBanner />
          </div>

          {/* Mobile Category Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-thin" style={{ WebkitOverflowScrolling: 'touch' }}>
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <button key={cat.id} data-testid={`mobile-category-${cat.id}`} onClick={() => { setActiveCategory(cat.id); setSelectedProvider("All"); setForceFlatView(false); if (cat.id === "slots") setActiveSlotSubcategory("all-slots"); }}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-colors duration-150 ${activeCategory === cat.id ? "text-white shadow-lg" : "bg-white/[0.04] text-white/60 border border-white/[0.06]"}`} style={activeCategory === cat.id ? CATEGORY_BTN_ACTIVE_STYLE : CATEGORY_BTN_INACTIVE_STYLE}>
                  <Icon className="w-3.5 h-3.5" />{cat.label}
                  {cat.badge && <span className={`px-1.5 py-0.5 text-[8px] rounded-sm font-black ${activeCategory === cat.id ? "bg-white/20 text-white" : "bg-red-500/90 text-white"}`}>{cat.badge}</span>}
                </button>
              );
            })}
          </div>

          {/* Mobile Provider Filter */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {PROVIDERS.map(provider => (
              <button key={provider} data-testid={`mobile-provider-${provider.toLowerCase().replace(/\s/g, '-')}`} onClick={() => setSelectedProvider(provider)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-md text-[11px] font-semibold whitespace-nowrap transition-all ${selectedProvider === provider ? "bg-white/10 text-white border border-white/20" : "text-white/40 border border-transparent"}`}>
                {provider === "All" ? "All Providers" : provider}
              </button>
            ))}
          </div>

          {/* Mobile Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none z-10" />
            <input
              ref={mobileSearchInputRef}
              type="search"
              enterKeyHint="search"
              placeholder="Search games — start typing…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              inputMode="search"
              className="w-full pl-10 pr-10 py-3 bg-white/[0.06] border border-white/[0.15] rounded-lg font-medium text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#4d7cff]/60 focus:bg-white/[0.09] focus:ring-2 focus:ring-[#4d7cff]/25 transition-all"
              style={{ fontSize: "16px", color: "#ffffff", WebkitTextFillColor: "#ffffff" }}
              data-testid="mobile-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white z-10"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mobile Content */}
          {searchQuery ? (
            <div>
              <p className="text-zinc-400 text-xs mb-3">Results for "{searchQuery}" ({filteredGames.length})</p>
              <div className="grid grid-cols-3 gap-2">
                {filteredGames.map((game, idx) => (
                  <div key={game.id || idx} onClick={() => playGame(game)} className="cursor-pointer group">
                    <div className="relative rounded-lg overflow-hidden aspect-square">
                      <img src={game.image || game.thumbnail} alt={game.name} className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-1.5"><p className="text-center text-[9px] font-medium text-white truncate" data-no-sanitize>{game.name}</p></div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredGames.length === 0 && <div className="text-center py-12"><Search className="w-8 h-8 text-zinc-700 mx-auto mb-2" /><p className="text-zinc-500 text-sm">No games found</p></div>}
            </div>
          ) : !isDefaultView ? (
            /* Filtered category/provider on mobile - show grid */
            <div>
              {selectedProvider !== "All" && (
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setSelectedProvider("All"); setActiveCategory("all"); }} className="text-zinc-400 hover:text-white" data-testid="mobile-back-to-all">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h3 className="text-sm font-bold text-white">{selectedProvider}</h3>
                    <span className="text-zinc-500 text-xs">({filteredGames.length} games)</span>
                  </div>
                </div>
              )}
              {activeCategory === "table" && (
                <div className="space-y-2 mb-4">
                  {[
                    { name: "Blackjack", path: "/game/blackjack", id: "blackjack", desc: "Beat the dealer at 21", color: "from-zinc-800 to-zinc-900" },
                    { name: "Roulette", path: "/game/roulette", id: "roulette", desc: "Spin the wheel", color: "from-green-900 to-emerald-900" },
                    { name: "Poker", path: "/game/poker", id: "poker", desc: "Texas Hold'em", color: "from-blue-900 to-indigo-900" },
                  ].filter(g => !disabledGameIds.includes(g.id)).map(g => (
                    <div key={g.name} onClick={() => navigate(g.path)} className={`p-3 rounded-xl bg-gradient-to-r ${g.color} border border-white/10 cursor-pointer active:scale-[0.98] transition-all`}>
                      <div className="flex items-center justify-between"><div><h3 className="text-sm font-bold text-white">{g.name}</h3><p className="text-zinc-400 text-[10px]">{g.desc}</p></div><Play className="w-5 h-5 text-[#4d7cff]" /></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                {filteredGames.map((game, idx) => (
                  <div key={game.id || idx} onClick={() => playGame(game)} className="cursor-pointer group">
                    <div className="relative rounded-lg overflow-hidden aspect-square ">
                      <img src={game.image || game.thumbnail} alt={game.name} className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-1.5"><p className="text-center text-[9px] font-semibold text-white truncate" data-no-sanitize>{game.name}</p></div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredGames.length === 0 && <div className="text-center py-12"><Gamepad2 className="w-8 h-8 text-zinc-700 mx-auto mb-2" /><p className="text-zinc-500 text-sm">{activeCategory === "favorites" ? "No favorites yet" : "No games found"}</p></div>}
            </div>
          ) : (
            /* Default mobile: provider rows */
            <>
              {/* Top Arcade Games - Mobile — MIXED pool from Mascot/Booming/Betsoft/Redwood.
                  Each game keeps its own provider so labels are correct and clicks route to
                  the right launcher (e.g. Betsoft's Gold Nugget Rush 2 opens via Betsoft, not Mascot). */}
              {featuredData?.top_arcade_games?.games?.length > 0 && (
                <div className="mb-4" data-testid="mobile-top-arcade">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Flame className="w-4 h-4 text-orange-400" />
                      <h3 className="text-sm font-bold text-white">Top Arcade Games</h3>
                      <span className="px-1.5 py-0.5 text-[7px] font-bold bg-orange-500/20 text-orange-400 rounded-full">WEEKLY</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {featuredData.top_arcade_games.games
                      .filter(g => !isProviderDisabled(g.provider || ""))
                      .slice(0, 9)
                      .map((game, idx) => (
                      <div key={game.id || game.gameId || idx} onClick={() => playGame(game)} className="cursor-pointer group">
                        <div className="relative rounded-lg overflow-hidden aspect-square ">
                          <img src={resolveCuratedThumb(game)} alt={game.name} className={`w-full h-full ${(game.provider || "").toLowerCase() === "mascot" ? "object-contain" : "object-cover"} transition-transform duration-500 group-hover:scale-105`} loading="lazy" onError={(e) => { e.currentTarget.src = JR_PLACEHOLDER; }} />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-1.5"><p className="text-[9px] font-semibold text-white truncate">{game.name}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Featured Games - Mobile */}
              {featuredData?.featured_games && (
                <div className="mb-4" data-testid="mobile-featured-games">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    <h3 className="text-sm font-bold text-white">Featured Games</h3>
                    <span className="px-1.5 py-0.5 text-[7px] font-bold bg-[#4d7cff]/20 text-[#4d7cff] rounded-full">4-DAY</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ...(featuredData.featured_games.booming || []).map(g => ({ ...g, provider: "Booming Games" })),
                      ...(featuredData.featured_games.betsoft || []).map(g => {
                        const backendImg = g.image || g.thumbnail || g.imageUrl || "";
                        const trustBackend = typeof backendImg === "string" && (backendImg.startsWith("/games/betsoft/") || backendImg.startsWith("/api/game-assets/games/betsoft/"));
                        const img = trustBackend ? backendImg : getBetsoftGameImage(g.gameId || g.id, g.name);
                        return { ...g, provider: "Betsoft", image: img, thumbnail: img };
                      }),
                      ...(featuredData.featured_games.petersons || []).map(g => ({ ...g, provider: "Redwood" })),
                      ...(featuredData.featured_games.spinjoy  || []).map(g => ({ ...g, provider: "Spinjoy" })),
                    ].slice(0, 9).map((game, idx) => (
                      <div key={game.id || game.gameId || idx} onClick={() => playGame(game)} className="cursor-pointer group">
                        <div className="relative rounded-lg overflow-hidden aspect-square ">
                          <img src={game.image || game.thumbnail} alt={game.name} className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-1.5"><p className="text-[9px] font-semibold text-white truncate">{game.name}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isProviderDisabled("Mascot") && seasonalSection && seasonalSection.games?.length > 0 && (
                <div
                  className={`mb-6 ${seasonalSection.theme === "graveyard" ? "jr-seasonal-graveyard" : ""}`}
                  data-testid="mobile-row-seasonal-graveyard"
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      {seasonalSection.mascot_slug && (
                        <Mascot
                          slug={seasonalSection.mascot_slug}
                          size="sm"
                          role="inline"
                          tip={seasonalSection.mascot_tip || seasonalSection.subtitle}
                          className="!w-9 !h-9 shrink-0"
                        />
                      )}
                      <div className="flex flex-col leading-tight">
                        <h3 className="jr-seasonal-title text-sm font-bold text-white uppercase tracking-wide" data-no-sanitize>{seasonalSection.name}</h3>
                        {seasonalSection.subtitle && (
                          <span className="text-[10px] text-purple-300/85 font-medium truncate">{seasonalSection.subtitle}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-hide -mx-3 px-3 snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {seasonalSection.games.map((game, idx) => (
                      <div
                        key={game.id || game.gameId || idx}
                        onClick={() => playGame({ ...game, isMascot: true })}
                        className="flex-shrink-0 w-[110px] cursor-pointer group active:scale-[0.97] transition-transform snap-start"
                        data-testid={`seasonal-graveyard-card-${game.id || game.gameId}`}
                      >
                        <div className="relative rounded-xl overflow-hidden aspect-square ring-1 ring-purple-500/40 shadow-[0_0_18px_rgba(139,92,246,0.35)]">
                          <img
                            src={game.image}
                            alt={game.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => { e.currentTarget.src = JR_PLACEHOLDER; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />
                          <div className="absolute bottom-0 left-0 right-0 p-1.5">
                            <p className="text-center text-[10px] font-bold text-white truncate leading-tight" data-no-sanitize>{game.name}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isProviderDisabled("Mascot") && <MobileRow title="Mascot Gaming" games={mascotGames} onGameClick={(g) => playGame({...g, isMascot: true})} onViewAll={() => setSelectedProvider("Mascot")} />}
              {!isProviderDisabled("KA Gaming") && kaGames.length > 0 && <MobileRow title="KA Gaming" games={kaGames} onGameClick={(g) => playGame({...g, isKA: true})} onViewAll={() => setSelectedProvider("KA Gaming")} />}
              {!isProviderDisabled("Booming") && <MobileRow title="Booming Games" games={boomingGames} onGameClick={(g) => playGame({...g, isBooming: true})} onViewAll={() => setSelectedProvider("Booming Games")} />}
              {!isProviderDisabled("Betsoft") && <MobileRow title="Betsoft" games={betsoftGames} onGameClick={(g) => playGame({...g, isBetsoft: true})} onViewAll={() => setSelectedProvider("Betsoft")} />}
              {!isProviderDisabled("Redwood") && <MobileRow title="Redwood" games={petersonsGames} onGameClick={(g) => playGame({...g, isPetersons: true})} onViewAll={() => setSelectedProvider("Redwood")} />}
              {canSeeSpinjoy && (
                spinjoyGames.length > 0
                  ? <MobileRow title="Spinjoy" games={spinjoyGames} onGameClick={(g) => playGame({...g, isSpinjoy: true})} onViewAll={() => setSelectedProvider("Spinjoy")} mascotSlug="astronaut" mascotTip="Spinjoy Live! Warp-speed spins ahead 🚀" />
                  : <div className="mb-6" data-testid="mobile-row-spinjoy-empty">
                      <div className="flex items-center justify-between mb-2.5">
                        <div className="flex items-center gap-2">
                          <Mascot slug="astronaut" size="sm" role="inline" tip="Spinjoy is syncing — hold tight 🚀" className="!w-8 !h-8 shrink-0" />
                          <h3 className="text-sm font-bold text-white">Spinjoy</h3>
                        </div>
                        <span className="text-[10px] font-semibold text-sky-400 uppercase tracking-wider">Syncing…</span>
                      </div>
                      <p className="text-[11px] text-zinc-500">Catalog is loading on this environment. Admins &amp; the Spinjoy cert account can trigger a sync from the admin dashboard.</p>
                    </div>
              )}
              {canSee3Oaks && (
                oaksGames.length > 0
                  ? <MobileRow title="3 Oaks" games={oaksGames} onGameClick={(g) => playGame({...g, isOaks: true})} onViewAll={() => setSelectedProvider("3 Oaks")} />
                  : (
                    // Sep 2026 owner directive: Admins ALWAYS see the "3 Oaks" row,
                    // even when the catalog sync is empty. Renders a placeholder card
                    // so the row is visible while Nazar's back-office whitelist is
                    // still pending — the hourly retry loop will populate it live.
                    <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-900/25 to-fuchsia-900/15 p-4" data-testid="mobile-row-oaks-empty-admin">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-bold text-white uppercase tracking-wide">3 Oaks</span>
                        <span className="px-2 py-0.5 text-[9px] font-bold bg-amber-500/20 text-amber-300 rounded-full border border-amber-500/30 uppercase">Admin Only · Syncing</span>
                      </div>
                      <p className="text-[11px] text-zinc-400">Waiting on 3 Oaks to whitelist our egress IP. The hourly retry loop will fill this row automatically the moment the games become reachable.</p>
                    </div>
                  )
              )}
              {!isProviderDisabled("JackRoyals") && !isProviderDisabled("Originals") && <MobileRow title="JackRoyals Originals" games={games.filter(g => JACKROYALS_PROVIDERS.includes(g.provider) && !disabledGameIds.includes(g.id) && !disabledGameIds.includes(String(g.id)) && !isProviderDisabled(g.provider))} onGameClick={(g) => playGame(g)} onViewAll={() => setSelectedProvider("JackRoyals")} />}            </>
          )}
        </div>

        {/* ══════════ DESKTOP LAYOUT ══════════ */}
        <div className="hidden md:block px-6 lg:px-10 pt-6 pb-16 max-w-[1440px] mx-auto relative">
          {/* Side Decorations - Arcade Glow */}
          <div className="hidden xl:block fixed left-0 top-20 bottom-0 w-16 pointer-events-none z-0 opacity-20">
            <div className="w-full h-full" style={{ background: 'linear-gradient(to right, rgba(77,124,255,0.15), transparent)' }} />
          </div>
          <div className="hidden xl:block fixed right-0 top-20 bottom-0 w-16 pointer-events-none z-0 opacity-20">
            <div className="w-full h-full" style={{ background: 'linear-gradient(to left, rgba(77,124,255,0.15), transparent)' }} />
          </div>

          {/* ── 🔥 Featured This Week + TOP Games (side-by-side on desktop) ─── */}
          {isDefaultView && ((featuredData?.featured?.length > 0) || (featuredData?.top?.length > 0)) && (
          <div
            className="mb-6 sm:mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6"
            data-testid="featured-and-top-row"
          >
          {/* ── 🔥 Featured This Week (admin-curated, top of page) ─────── */}
          {featuredData?.featured?.length > 0 && (
            <div className="min-w-0" data-testid="featured-this-week-top">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">Featured This Week</h3>
                  <span className="px-2 py-0.5 text-[9px] font-bold bg-amber-500/20 text-amber-400 rounded-full border border-amber-500/30 uppercase">Curated</span>
                </div>
                <button
                  onClick={() => setActiveCategory("featured")}
                  className="text-amber-400 text-sm font-semibold flex items-center gap-0.5 hover:underline"
                  data-testid="view-all-featured-top"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2 sm:gap-3">
                {featuredData.featured.slice(0, 12).map((game) => {
                  const thumb = resolveCuratedThumb(game);
                  return (
                  <div
                    key={game.game_id}
                    onClick={() => playGame({
                      id: game.game_id,
                      gameId: game.game_id,
                      name: game.name,
                      provider: game.provider,
                      thumbnail: thumb,
                    })}
                    className="cursor-pointer group"
                    data-testid={`featured-top-card-${game.game_id}`}
                  >
                    <div className="relative rounded-lg sm:rounded-xl overflow-hidden aspect-square bg-black/40 ring-1 ring-amber-500/20 hover:ring-amber-400/60 transition-all hover:scale-[1.03]">
                      <img
                        src={thumb}
                        alt={game.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => { e.target.style.opacity = 0.2; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                      {game.badge && (
                        <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-amber-500 text-black uppercase tracking-wider">
                          {game.badge}
                        </span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                        <p className="text-center text-[9px] sm:text-xs font-semibold text-white truncate">{game.name}</p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* ── 🔥 TOP Games (admin-curated, sibling to Featured) ─────── */}
          {featuredData?.top?.length > 0 && (            <div className="min-w-0" data-testid="top-games-curated-section">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-rose-400" />
                  <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">TOP Games</h3>
                  <span className="px-2 py-0.5 text-[9px] font-bold bg-rose-500/20 text-rose-400 rounded-full border border-rose-500/30 uppercase">Top Picks</span>
                </div>
                <button
                  onClick={() => setActiveCategory("top")}
                  className="text-rose-400 text-sm font-semibold flex items-center gap-0.5 hover:underline"
                  data-testid="view-all-top-curated"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2 sm:gap-3">
                {(() => {
                  // FEATURED PINNING (Feb 2026 ops directive): any game whose
                  // launch date is inside the last 7 days rides the front of
                  // the TOP Games carousel, regardless of the admin-curated
                  // order. Also auto-injects the freshest NEW-window title
                  // if the admin hasn't added it to the top list yet.
                  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
                  const nowMs = Date.now();
                  const provKey = (p) => (p || "").toLowerCase().replace(/[^a-z]/g, "");
                  const launchTsFor = (game) => {
                    try {
                      if (typeof window !== "undefined" && window.__jrNewLaunchDates) {
                        // curated entries store `provider` as display name; map to internal keys
                        const pk = provKey(game.provider);
                        const bucket = window.__jrNewLaunchDates[pk === "petersons" ? "petersons" : pk === "kagaming" ? "ka" : pk === "boominggames" ? "booming" : pk === "mascot" ? "mascot" : pk === "betsoft" ? "betsoft" : pk === "spinjoy" ? "spinjoy" : pk];
                        if (bucket && bucket[String(game.game_id)]) return bucket[String(game.game_id)];
                      }
                      if (game.released_at) return Date.parse(game.released_at) || 0;
                      if (game.releaseDate) return Date.parse(game.releaseDate) || 0;
                      if (game.created_at) return Date.parse(game.created_at) || 0;
                    } catch (_e) {}
                    return 0;
                  };

                  // Dedupe the admin-curated TOP list upfront so a game added
                  // twice (e.g. "Shopping" appearing two times, or the same
                  // title stored under slightly different provider casing)
                  // renders once. Primary key = provider|game_id; secondary
                  // fallback key = lowercased name so cross-casing / cross-
                  // provider duplicates are also caught.
                  const seenKeys = new Set();
                  const seenNames = new Set();
                  const curated = [];
                  (featuredData.top || []).forEach((g) => {
                    const k = `${provKey(g.provider)}|${g.game_id}`;
                    const nk = (g.name || "").trim().toLowerCase();
                    if (seenKeys.has(k)) return;
                    if (nk && seenNames.has(nk)) return;
                    seenKeys.add(k);
                    if (nk) seenNames.add(nk);
                    curated.push(g);
                  });
                  const curatedKeys = new Set(curated.map(g => `${provKey(g.provider)}|${g.game_id}`));
                  try {
                    (newLaunchItems || []).forEach(item => {
                      const ts = Date.parse(item.launch_date) || 0;
                      if (nowMs - ts > SEVEN_DAYS_MS) return; // only inject fresh
                      const providerLabel = item.provider === "betsoft" ? "Betsoft"
                        : item.provider === "booming" ? "Booming Games"
                        : item.provider === "petersons" ? "Redwood"
                        : item.provider === "mascot" ? "Mascot"
                        : item.provider === "ka" ? "KA Gaming"
                        : item.provider === "spinjoy" ? "Spinjoy"
                        : "JackRoyals";
                      const key = `${provKey(providerLabel)}|${item.game_id}`;
                      const nk = (item.name || "").trim().toLowerCase();
                      if (curatedKeys.has(key)) return;
                      if (nk && seenNames.has(nk)) return;
                      curatedKeys.add(key);
                      if (nk) seenNames.add(nk);
                      curated.push({
                        game_id: item.game_id,
                        name: item.name,
                        provider: providerLabel,
                        badge: "NEW",
                        released_at: item.launch_date,
                      });
                    });
                  } catch (_e) {}

                  // Sort: fresh (<7d) first (newest first among the fresh); then curated original order.
                  curated.sort((a, b) => {
                    const aFresh = (nowMs - launchTsFor(a)) <= SEVEN_DAYS_MS && launchTsFor(a) > 0;
                    const bFresh = (nowMs - launchTsFor(b)) <= SEVEN_DAYS_MS && launchTsFor(b) > 0;
                    if (aFresh !== bFresh) return aFresh ? -1 : 1;
                    if (aFresh && bFresh) return launchTsFor(b) - launchTsFor(a);
                    return 0; // preserve admin-curated ordering
                  });

                  return curated.slice(0, 12).map((game) => {
                    const thumb = resolveCuratedThumb(game);
                    const isFresh = (nowMs - launchTsFor(game)) <= SEVEN_DAYS_MS && launchTsFor(game) > 0;
                    return (
                  <div
                    key={`${provKey(game.provider)}-${game.game_id}`}
                    onClick={() => playGame({
                      id: game.game_id,
                      gameId: game.game_id,
                      name: game.name,
                      provider: game.provider,
                      thumbnail: thumb,
                    })}
                    className="cursor-pointer group"
                    data-testid={`top-curated-card-${game.game_id}`}
                  >
                    <div className={`relative rounded-lg sm:rounded-xl overflow-hidden aspect-square bg-black/40 ring-1 transition-all hover:scale-[1.03] ${isFresh ? "ring-emerald-400/60 hover:ring-emerald-300" : "ring-rose-500/20 hover:ring-rose-400/60"}`}>
                      <img
                        src={thumb}
                        alt={game.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => { e.target.style.opacity = 0.2; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                      {isFresh ? (
                        <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-emerald-500 text-black uppercase tracking-wider">
                          NEW
                        </span>
                      ) : game.badge ? (
                        <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-rose-500 text-white uppercase tracking-wider">
                          {game.badge}
                        </span>
                      ) : (
                        <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-rose-500 text-white uppercase tracking-wider">
                          TOP
                        </span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                        <p className="text-center text-[9px] sm:text-xs font-semibold text-white truncate">{game.name}</p>
                      </div>
                    </div>
                  </div>
                  );
                });
                })()}
              </div>
            </div>
          )}
          </div>
          )}

          {/* ── 🪦 THE JACKPOT GRAVEYARD (Feb 2026 seasonal, admin-toggleable) ─────
                Dark purple glow + slow mist animation (styled via .jr-seasonal-graveyard),
                fronted by the Wizard mascot as the "Master of the Graveyard". Admin can
                rename/retheme/repoint via PUT /api/admin/seasonal-section without a
                frontend deploy. Only renders when enabled & games are non-empty. */}
          {isDefaultView && seasonalSection && seasonalSection.games?.length > 0 && (
            <div
              className={`mb-6 sm:mb-8 ${seasonalSection.theme === "graveyard" ? "jr-seasonal-graveyard" : ""}`}
              data-testid="desktop-seasonal-graveyard"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-3">
                  {seasonalSection.mascot_slug && (
                    <Mascot
                      slug={seasonalSection.mascot_slug}
                      size="md"
                      role="inline"
                      tip={seasonalSection.mascot_tip || seasonalSection.subtitle}
                      className="!w-12 !h-12 shrink-0"
                    />
                  )}
                  <div className="flex flex-col leading-tight">
                    <h3
                      className="jr-seasonal-title text-base sm:text-lg font-black text-white uppercase tracking-wider"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                      data-no-sanitize
                    >
                      {seasonalSection.name}
                    </h3>
                    {seasonalSection.subtitle && (
                      <span className="text-[11px] sm:text-xs text-purple-300/90 font-semibold">
                        {seasonalSection.subtitle}
                      </span>
                    )}
                  </div>
                  <span className="px-2 py-0.5 text-[9px] font-bold bg-purple-500/25 text-purple-200 rounded-full border border-purple-400/50 uppercase tracking-wider">
                    Seasonal
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                {seasonalSection.games.map((game, idx) => (
                  <div
                    key={game.id || game.gameId || idx}
                    onClick={() => playGame({ ...game, isMascot: true })}
                    className="cursor-pointer group"
                    data-testid={`seasonal-graveyard-card-${game.id || game.gameId}`}
                  >
                    <div className="relative rounded-lg sm:rounded-xl overflow-hidden aspect-square bg-black/40 ring-1 ring-purple-500/40 hover:ring-purple-300/80 transition-all hover:scale-[1.03] shadow-[0_0_18px_rgba(139,92,246,0.35)] hover:shadow-[0_0_28px_rgba(199,172,255,0.55)]">
                      <img
                        src={game.image}
                        alt={game.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = JR_PLACEHOLDER; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent pointer-events-none" />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-purple-500 text-white uppercase tracking-wider">
                          Headliner
                        </span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                        <p className="text-center text-[9px] sm:text-xs font-semibold text-white truncate" data-no-sanitize>{game.name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ✨ NEW GAMES (auto-populated from /api/new-game-launches, 14-day window) ───────
                Positioned RIGHT AFTER Featured (top of the lobby) so freshly-launched
                titles are visible above the fold — 2026-08-19 UX move per Nainer batch feedback. */}

          {isDefaultView && newLaunchItems.length > 0 && (() => {
            // Resolve each launch entry to a full game object from the loaded provider pools.
            // Falls back to a synthetic tile if the game isn't in a loaded pool yet.
            const resolveNewGame = (item) => {
              const p = (item.provider || "").toLowerCase();
              const gid = String(item.game_id);
              let found = null;
              if (p === "booming")   found = boomingGames.find(g => String(g.id) === gid || String(g.gameId) === gid);
              else if (p === "betsoft")   found = betsoftGames.find(g => String(g.gameId) === gid || String(g.id) === gid);
              else if (p === "mascot")    found = mascotGames.find(g => String(g.id) === gid || String(g.gameId) === gid);
              else if (p === "petersons") found = petersonsGames.find(g => String(g.id) === gid);
              else if (p === "ka")        found = kaGames.find(g => String(g.gameId) === gid || String(g.id) === gid);
              if (found) return { ...found, provider: found.provider || item.provider };
              // Synthetic fallback (provider pool not loaded yet) — still clickable
              const kaOverride = p === "ka" ? (KA_THUMB_OVERRIDES[gid] || KA_THUMB_OVERRIDES[item.name?.replace(/\s+/g, "")]) : null;
              return {
                id: gid,
                gameId: gid,
                name: item.name || "New Game",
                thumbnail: kaOverride || "",
                image: kaOverride || "",
                provider: item.provider === "booming" ? "Booming"
                        : item.provider === "betsoft" ? "Betsoft"
                        : item.provider === "mascot" ? "Mascot"
                        : item.provider === "petersons" ? "Redwood"
                        : item.provider === "ka" ? "KA Gaming"
                        : item.provider,
                isKA: item.provider === "ka",
                isBooming: item.provider === "booming",
                isBetsoft: item.provider === "betsoft",
                isMascot: item.provider === "mascot",
                isPetersons: item.provider === "petersons",
              };
            };
            const cards = newLaunchItems.slice(0, 12).map(resolveNewGame);
            return (
              <div className="mb-6 sm:mb-8" data-testid="new-games-section">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">New Games</h3>
                    <span className="px-2 py-0.5 text-[9px] font-bold bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30 uppercase">Just Launched</span>
                  </div>
                  <button
                    onClick={() => setActiveCategory("new")}
                    className="text-emerald-400 text-sm font-semibold flex items-center gap-0.5 hover:underline"
                    data-testid="view-all-new-games"
                  >
                    View All <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                  {cards.map((game, idx) => {
                    const thumb = resolveCuratedThumb(game);
                    return (
                      <div
                        key={game.id || game.gameId || idx}
                        onClick={() => playGame(game)}
                        className="cursor-pointer group"
                        data-testid={`new-game-card-${game.id || game.gameId}`}
                      >
                        <div className="relative rounded-lg sm:rounded-xl overflow-hidden aspect-square bg-black/40 ring-1 ring-emerald-500/20 hover:ring-emerald-400/60 transition-all hover:scale-[1.03]">
                          <img
                            src={thumb || "/api/game-assets/games/betsoft/_placeholder.jpg"}
                            alt={game.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.src = JR_PLACEHOLDER; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                          <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-emerald-500 text-black uppercase tracking-wider">
                            NEW
                          </span>
                          <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                            <p className="text-center text-[9px] sm:text-xs font-semibold text-white truncate">{game.name}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── 🎣 Shooting Fish Games (KA Gaming — auto-filtered by category) ─────── */}
          {isDefaultView && (() => {
            // Pull every KA Gaming game classified as "shooting" (fish hunters,
            // cannon/shooter games, dragon/octopus hunters). Also include any
            // other provider game that classifies as "shooting" for future-proof.
            const shootingGames = [
              ...kaGames.map(g => ({ ...g, provider: "KA Gaming", isKA: true, category: classifyGame(g) })),
              ...boomingGames.map(g => ({ ...g, provider: "Booming", isBooming: true, category: classifyGame(g) })),
              ...betsoftGames.map(g => ({ ...g, provider: "Betsoft", isBetsoft: true, category: classifyGame(g) })),
              ...mascotGames.map(g => ({ ...g, provider: "Mascot", isMascot: true, category: classifyGame(g) })),
              ...petersonsGames.map(g => ({ ...g, provider: "Redwood", isPetersons: true, category: classifyGame(g) })),
            ].filter(g => g.category === "shooting");
            if (shootingGames.length === 0) return null;
            return (
              <div className="mb-6 sm:mb-8" data-testid="shooting-fish-section">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center gap-2">
                    <Fish className="w-5 h-5 text-cyan-400" />
                    <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">Shooting Fish Games</h3>
                    <span className="px-2 py-0.5 text-[9px] font-bold bg-cyan-500/20 text-cyan-400 rounded-full border border-cyan-500/30 uppercase">Hot</span>
                  </div>
                  <button
                    onClick={() => setActiveCategory("shooting")}
                    className="text-cyan-400 text-sm font-semibold flex items-center gap-0.5 hover:underline"
                    data-testid="view-all-shooting-fish"
                  >
                    View All <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                  {shootingGames.slice(0, 12).map((game, idx) => {
                    const thumb = game.thumbnail || game.image || game.imageUrl || resolveCuratedThumb(game);
                    return (
                      <div
                        key={game.id || game.gameId || idx}
                        onClick={() => playGame(game)}
                        className="cursor-pointer group"
                        data-testid={`shooting-fish-card-${game.id || game.gameId}`}
                      >
                        <div className="relative rounded-lg sm:rounded-xl overflow-hidden aspect-square bg-black/40 ring-1 ring-cyan-500/20 hover:ring-cyan-400/60 transition-all hover:scale-[1.03]">
                          <img
                            src={thumb || "/api/game-assets/games/betsoft/_placeholder.jpg"}
                            alt={game.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.src = JR_PLACEHOLDER; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                          <span className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 text-[7px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 rounded bg-cyan-500 text-black uppercase tracking-wider">
                            FISH
                          </span>
                          <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                            <p className="text-center text-[9px] sm:text-xs font-semibold text-white truncate">{game.name}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}


          {/* ── Coming Soon providers block removed per owner directive (Feb 2026) ── */}

          {isDefaultView && (
            <div className="mb-10 relative" data-testid="hero-banner">
              {/* Main Hero Card */}
              <div className="relative overflow-hidden rounded-2xl border border-[#1f2a4d]" style={{ background: 'radial-gradient(ellipse at top, #151b3a 0%, #050814 60%, #02030a 100%)' }}>
                {/* Animated background particles */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute w-96 h-96 -top-48 -left-48 rounded-full blur-[100px] animate-pulse" style={{ background: 'rgba(77,124,255,0.07)' }} />
                  <div className="absolute w-72 h-72 -bottom-36 -right-36 rounded-full blur-[80px] animate-pulse" style={{ background: 'rgba(0,212,255,0.06)', animationDelay: '1s' }} />
                  {/* Grid pattern overlay */}
                  <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(77,124,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(77,124,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                </div>

                <div className="relative px-8 lg:px-12 py-8 lg:py-10">
                  {/* Top Row: Badge + Playing Now */}
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      {/* User Tier Badge */}
                      {user?.vip_rank && (() => {
                        const tierColors = {
                          Bronze: { bg: "from-[#c47a3a] to-[#8b4f1f]", text: "#c47a3a", glow: "shadow-[#c47a3a]/30" },
                          Silver: { bg: "from-[#c9d1d9] to-[#8f96a0]", text: "#c9d1d9", glow: "shadow-slate-400/30" },
                          Gold: { bg: "from-[#ffd86b] to-[#b87412]", text: "#ffd86b", glow: "shadow-yellow-500/30" },
                          Platinum: { bg: "from-[#aeb4c8] to-[#6b7280]", text: "#aeb4c8", glow: "shadow-slate-300/30" },
                          Diamond: { bg: "from-[#6d99ff] to-[#4338ca]", text: "#6d99ff", glow: "shadow-blue-500/30" },
                          Ruby: { bg: "from-[#dc2850] to-[#991b1b]", text: "#dc2850", glow: "shadow-red-500/30" },
                          Royal: { bg: "from-[#4d7cff] to-[#1e40af]", text: "#4d7cff", glow: "shadow-[#4d7cff]/40" },
                        };
                        const tc = tierColors[user.vip_rank] || tierColors.Bronze;
                        return (
                          <div data-testid="lobby-tier-badge" className={`flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-lg ${tc.glow}`} style={{ background: `linear-gradient(135deg, ${tc.text}20, ${tc.text}08)`, borderColor: `${tc.text}40` }}>
                            <Crown className="w-3.5 h-3.5" style={{ color: tc.text }} />
                            <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: tc.text }}>{user.vip_rank} Tier</span>
                          </div>
                        );
                      })()}
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: 'rgba(77,124,255,0.08)', borderColor: 'rgba(77,124,255,0.2)' }}>
                        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: '#9fb5ff' }}>{playingNow} Playing Now</span>
                      </div>
                      <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.06)' }}>
                        <Sparkles className="w-3 h-3" style={{ color: '#4d7cff' }} />
                        <span className="text-[11px] font-medium text-zinc-400">{allCombined.length}+ Games Available</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: 'rgba(77,124,255,0.06)', borderColor: 'rgba(77,124,255,0.2)' }}>
                      <Gamepad2 className="w-3.5 h-3.5" style={{ color: '#4d7cff' }} />
                      <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: '#9fb5ff' }}>Arcade Sweepstakes</span>
                    </div>
                  </div>

                  {/* Center Content Grid */}
                  <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center">
                    {/* Left: Text + CTAs */}
                    <div className="col-span-12 lg:col-span-5">
                      <div className="flex items-center gap-4 mb-4">
                        <img src="https://customer-assets.emergentagent.com/job_dc2ea8e9-88c1-4191-a09e-3c70ac39f874/artifacts/lxlb74jo_IMG_9876.jpeg" alt="JR Crown" className="w-14 h-14 rounded-full object-cover drop-shadow-[0_0_18px_rgba(77,124,255,0.5)]" />
                      </div>
                      <h1 className="text-3xl lg:text-4xl xl:text-5xl font-black text-white tracking-tight leading-[1.1] mb-4">
                        Your Arcade
                        <br />
                        <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, #4d7cff, #00d4ff)' }}>Adventure</span>
                        <br />
                        Awaits
                      </h1>
                      <p className="text-sm lg:text-base max-w-sm mb-6 leading-relaxed" style={{ color: '#8fa2d6' }}>
                        The premier U.S. sweepstakes arcade. Play top games & win real prizes.
                      </p>
                      <div className="flex items-center gap-3">
                        <Button onClick={() => navigate("/store")} className="text-white font-bold px-6 py-3 rounded-xl shadow-lg transition-all hover:scale-[1.02]" style={{ background: '#4d7cff', boxShadow: '0 0 20px rgba(77,124,255,0.3)' }} data-testid="hero-get-coins-btn">
                          <Gift className="w-4 h-4 mr-2" /> Get Coins
                        </Button>
                        <Button onClick={() => {
                          const gamesSection = document.querySelector('[data-testid="section-new-games"]');
                          if (gamesSection) gamesSection.scrollIntoView({ behavior: 'smooth' });
                        }} variant="outline" className="border-[#1f2a4d] text-white hover:bg-white/5 px-5 py-3 rounded-xl transition-all" data-testid="hero-play-btn">
                          <Gamepad2 className="w-4 h-4 mr-2" /> Browse Games
                        </Button>
                      </div>
                    </div>

                    {/* Right: Reward Pool Cards */}
                    <div className="col-span-12 lg:col-span-7">
                      {/* Grand Prize - Large Card */}
                      <div onClick={() => navigate("/jackpots")} className="cursor-pointer group mb-4 relative overflow-hidden rounded-xl border transition-all hover:scale-[1.01]" style={{ borderColor: 'rgba(77,124,255,0.2)', background: 'linear-gradient(135deg, rgba(77,124,255,0.08) 0%, rgba(0,0,0,0.4) 100%)' }}>
                        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top right, rgba(77,124,255,0.12), transparent 60%)' }} />
                        <div className="relative px-6 py-5 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Sparkles className="w-4 h-4" style={{ color: '#4d7cff' }} />
                              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#4d7cff' }}>Grand Prize</span>
                              <span className="px-2 py-0.5 rounded text-[9px] font-black text-white uppercase tracking-wider" style={{ background: '#4d7cff' }}>Top</span>
                            </div>
                            <p className="text-3xl lg:text-4xl font-black text-white tabular-nums tracking-tight">
                              <TickingJackpot base={jackpots.mega} step={0.15} /> <span className="text-lg font-bold" style={{ color: '#4d7cff' }}>SC</span>
                            </p>
                          </div>
                          <div className="hidden sm:flex items-center gap-2 transition-colors" style={{ color: 'rgba(77,124,255,0.6)' }}>
                            <span className="text-xs font-medium">View Prizes</span>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>

                      {/* Minor Prizes Row */}
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: "MAJOR", base: jackpots.major, step: 0.08, color: '#00d4ff', borderColor: 'rgba(0,212,255,0.15)', bgColor: 'rgba(0,212,255,0.06)', icon: Trophy },
                          { label: "MINOR", base: jackpots.minor, step: 0.03, color: '#00ff88', borderColor: 'rgba(0,255,136,0.15)', bgColor: 'rgba(0,255,136,0.06)', icon: Zap },
                        ].map(j => (
                          <div key={j.label} onClick={() => navigate("/jackpots")} className="cursor-pointer group relative overflow-hidden rounded-xl border transition-all hover:scale-[1.01]" style={{ borderColor: j.borderColor, background: `linear-gradient(135deg, ${j.bgColor} 0%, rgba(0,0,0,0.3) 100%)` }}>
                            <div className="px-5 py-4 flex items-center justify-between">
                              <div>
                                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: j.color }}>{j.label}</p>
                                <p className="text-xl font-black text-white tabular-nums"><TickingJackpot base={j.base} step={j.step} /> <span className="text-zinc-500 text-xs">SC</span></p>
                              </div>
                              <j.icon className="w-5 h-5" style={{ color: `${j.color}30` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Giveaway Banner - Desktop ──────────────────────── */}
          {isDefaultView && (
            <div className="mb-4">
              <GiveawayLobbyBanner />
            <WelcomeBonusBanner />
            </div>
          )}

          {/* ── Daily Rewards Banner ──────────────────────── */}
          {isDefaultView && (
            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="daily-rewards-banner">
              {/* Daily Bonus Card */}
              <div
                onClick={() => navigate("/rewards")}
                className="cursor-pointer group relative overflow-hidden rounded-xl border transition-all hover:scale-[1.01]"
                style={{ borderColor: 'rgba(245,158,11,0.25)', background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(0,0,0,0.4) 100%)' }}
                data-testid="lobby-daily-bonus-card"
              >
                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top left, rgba(245,158,11,0.1), transparent 60%)' }} />
                <div className="relative px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
                      <Gift className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-amber-400 mb-0.5">Daily Bonus</p>
                      <p className="text-lg font-black text-white">Claim Free Coins</p>
                      <p className="text-[10px] text-zinc-500">Scales with your VIP tier + streak bonus</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 transition-colors" style={{ color: 'rgba(245,158,11,0.6)' }}>
                    {!user?.daily_bonus_claimed && <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />}
                    <ChevronRight className="w-5 h-5" />
                  </div>
                </div>
              </div>

              {/* Daily Spin Card */}
              <div
                onClick={() => navigate("/daily-wheel")}
                className="cursor-pointer group relative overflow-hidden rounded-xl border transition-all hover:scale-[1.01]"
                style={{ borderColor: 'rgba(168,85,247,0.25)', background: 'linear-gradient(135deg, rgba(168,85,247,0.08) 0%, rgba(0,0,0,0.4) 100%)' }}
                data-testid="lobby-daily-spin-card"
              >
                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top right, rgba(168,85,247,0.1), transparent 60%)' }} />
                <div className="relative px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.15)' }}>
                      <Sparkles className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-purple-400 mb-0.5">Daily Spin</p>
                      <p className="text-lg font-black text-white">Spin the Wheel</p>
                      <p className="text-[10px] text-zinc-500">Win SC + GC based on your tier level</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 transition-colors" style={{ color: 'rgba(168,85,247,0.6)' }}>
                    <ChevronRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Search + Category Bar ──────────────────────── */}
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none z-10" />
              <input
                ref={searchInputRef}
                type="search"
                enterKeyHint="search"
                placeholder="Search all games — start typing…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                inputMode="search"
                className="w-full pl-10 pr-10 py-2.5 bg-white/[0.05] border border-white/[0.12] rounded-xl font-medium text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#4d7cff]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#4d7cff]/25 transition-all"
                style={{ fontSize: "16px", color: "#ffffff", WebkitTextFillColor: "#ffffff" }}
                data-testid="desktop-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white z-10"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin pb-2">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button key={cat.id} data-testid={`category-${cat.id}`}
                    onClick={() => { setActiveCategory(cat.id); setSelectedProvider("All"); setForceFlatView(false); if (cat.id === "slots") setActiveSlotSubcategory("all-slots"); }}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl whitespace-nowrap transition-colors duration-150 text-sm font-medium ${
                      activeCategory === cat.id
                        ? "text-white font-bold shadow-lg"
                        : "text-zinc-400 hover:text-white hover:bg-white/[0.05]"
                    }`} style={activeCategory === cat.id ? CATEGORY_BTN_ACTIVE_STYLE_DESKTOP : CATEGORY_BTN_INACTIVE_STYLE}>
                    <Icon className="w-3.5 h-3.5" />{cat.label}
                    {cat.badge && <span className={`px-1.5 py-0.5 text-[9px] rounded font-bold ${activeCategory === cat.id ? "bg-white/20 text-white" : "bg-red-500/80 text-white"}`}>{cat.badge}</span>}
                    {cat.id === "favorites" && favorites.length > 0 && <span className="px-1.5 py-0.5 text-[9px] rounded-full bg-red-500 text-white font-bold">{favorites.length}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Provider Chips ─────────────────────────────── */}
          <div className="flex items-center gap-2 mb-6">
            {[
              { name: "Mascot",        color: "purple",  count: mascotGames.length },
              { name: "KA Gaming",     color: "sky",     count: kaGames.length },
              { name: "Betsoft",       color: "cyan",    count: betsoftGames.length },
              { name: "Booming Games", color: "emerald", count: boomingGames.length },
              { name: "Redwood",  color: "rose",    count: petersonsGames.length },
              { name: "JackRoyals",    color: "amber",   count: games.filter(g => JACKROYALS_PROVIDERS.includes(g.provider)).length },
              // Admin/tester-only chips (Feb 2026 — Spinjoy + 3 Oaks are hidden
              // from regular players but must remain filterable for the cert
              // teams so they can drill into their catalog on desktop too).
              ...(canSeeSpinjoy ? [{ name: "Spinjoy", color: "sky",   count: spinjoyGames.length }] : []),
              ...(canSee3Oaks   ? [{ name: "3 Oaks",  color: "amber", count: oaksGames.length    }] : []),
            ].filter(p => !isProviderDisabled(p.name)).map(p => {
              const isActive = selectedProvider === p.name;
              const colorMap = { purple: "bg-purple-500", sky: "bg-sky-500", cyan: "bg-cyan-500", emerald: "bg-emerald-500", rose: "bg-rose-500", amber: "bg-amber-500" };
              return (
                <button key={p.name} data-testid={`provider-chip-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setSelectedProvider(isActive ? 'All' : p.name)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                    isActive ? `${colorMap[p.color]} text-black font-bold shadow-lg` : 'bg-white/[0.03] border border-white/[0.06] text-zinc-400 hover:text-white hover:bg-white/[0.06]'
                  }`}>
                  <span className={`w-2 h-2 rounded-full ${colorMap[p.color]} ${isActive ? 'bg-black' : ''}`} />
                  {p.name}
                  {p.count > 0 && <span className={`text-xs ${isActive ? 'text-black/60' : 'text-zinc-600'}`}>({p.count})</span>}
                </button>
              );
            })}
            {selectedProvider !== "All" && (
              <button onClick={() => setSelectedProvider("All")} className="text-zinc-500 text-sm hover:text-white ml-1">Clear</button>
            )}
          </div>

          {/* ── Slot Subcategories ─────────────────────────── */}
          {activeCategory === "slots" && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" style={{ color: '#4d7cff' }} />
                <span className="text-white font-semibold text-sm">Browse by Type</span>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {SLOT_SUBCATEGORIES.map(subcat => {
                  const Icon = subcat.icon;
                  return (
                    <button key={subcat.id} onClick={() => setActiveSlotSubcategory(subcat.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl whitespace-nowrap transition-all text-xs font-medium ${
                        activeSlotSubcategory === subcat.id
                          ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-lg shadow-purple-500/25"
                          : "bg-white/[0.03] text-zinc-400 hover:bg-white/[0.06] hover:text-white border border-white/[0.05]"
                      }`}>
                      <Icon className="w-3.5 h-3.5" />{subcat.label}
                      {subcat.badge && <span className={`px-1.5 py-0.5 text-[9px] rounded ${activeSlotSubcategory === subcat.id ? "bg-white/20 text-white" : "bg-[#4d7cff]/80 text-white"}`}>{subcat.badge}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Loading State ─────────────────────────────── */}
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#4d7cff]/30 border-t-[#4d7cff]" />
                <div className="absolute inset-0 flex items-center justify-center"><Sparkles className="w-5 h-5 text-[#4d7cff] animate-pulse" /></div>
              </div>
            </div>
          ) : isDefaultView ? (
            /* ═══════ DEFAULT HOME VIEW: Grid Sections (Funrize-style) ═══════ */
            <div className="space-y-2">

              {/* ── Favorites Section ─────────────────── */}
              {favorites.length > 0 && (() => {
                const favGames = allCombined.filter(g => favorites.includes(g.id));
                return favGames.length > 0 ? (
                  <GridSection
                    title="My Favorites"
                    icon={Heart}
                    games={favGames}
                    totalCount={favGames.length}
                    onGameClick={playGame}
                    onFavorite={toggleFavorite}
                    favorites={favorites}
                    visibleCount={6}
                    onViewAll={() => setActiveCategory("favorites")}
                  />
                ) : null;
              })()}

              {/* ── Betsoft July 30% OFF Promo removed per ops 2026-06-30 ── */}

              {/* ── New Games (Top 10 per provider, freshest launch_date first) ───── */}
              {(() => {
                const providerGroups = {};
                allCombined.forEach(g => {
                  const p = g.isMascot ? "Mascot" : g.isBooming ? "Booming Games" : g.isBetsoft ? "Betsoft" : g.isPetersons ? "Redwood" : g.isSpinjoy ? "Spinjoy" : (g.provider || "JackRoyals");
                  if (!providerGroups[p]) providerGroups[p] = [];
                  providerGroups[p].push(g);
                });
                // Sort each provider's pool by (isNewLaunch first) then by
                // launch_date DESC — so the freshest release always occupies
                // slot #1. This is the auto-priority behavior: adding a title
                // with today's date to NEW_LAUNCHES bubbles it to the top
                // automatically without any manual re-ordering.
                Object.keys(providerGroups).forEach(p => {
                  providerGroups[p].sort((a, b) => {
                    const aN = isNewLaunch(a) ? 0 : 1;
                    const bN = isNewLaunch(b) ? 0 : 1;
                    if (aN !== bN) return aN - bN;
                    // Within the same NEW-or-not bucket, most recently
                    // launched wins.
                    return newLaunchTimestamp(b) - newLaunchTimestamp(a);
                  });
                  providerGroups[p] = providerGroups[p].slice(0, 10);
                });
                // Interleave providers to preserve variety, BUT keep the
                // freshest game (by launch date) at absolute #1 across all
                // providers so a brand-new title is unambiguously the hero.
                const providers = Object.values(providerGroups);
                const newGames = [];
                const maxLen = Math.max(...providers.map(p => p.length), 0);
                for (let i = 0; i < maxLen; i++) {
                  for (const pg of providers) {
                    if (i < pg.length) newGames.push(pg[i]);
                  }
                }
                // Final pass: bubble the single globally-newest game to #0.
                if (newGames.length > 1) {
                  let bestIdx = 0;
                  let bestTs = newLaunchTimestamp(newGames[0]);
                  for (let i = 1; i < newGames.length; i++) {
                    const ts = newLaunchTimestamp(newGames[i]);
                    if (ts > bestTs) { bestTs = ts; bestIdx = i; }
                  }
                  if (bestIdx > 0) {
                    const [hero] = newGames.splice(bestIdx, 1);
                    newGames.unshift(hero);
                  }
                }
                return newGames.length > 0 ? (
                  <GridSection
                    title="New Games"
                    icon={PartyPopper}
                    games={newGames}
                    totalCount={newGames.length}
                    onGameClick={playGame}
                    onFavorite={toggleFavorite}
                    favorites={favorites}
                    visibleCount={6}
                    onViewAll={() => setActiveCategory("new")}
                  />
                ) : null;
              })()}

              {/* ── Top Games ─────────────────────────── */}
              {(() => {
                // Base pool: whatever the admin curates as "popular".
                const topGames = allCombined.filter(g => popularGameIds.includes(String(g.gameId || g.id)));
                // Auto-inject any game currently inside the NEW window (Feb
                // 2026 ops directive) so a fresh release like The Game Show
                // rides Top Games even before the admin marks it popular.
                const alreadyInTop = new Set(topGames.map(g => `${g.provider || ""}|${g.gameId || g.id}`));
                const freshInjections = allCombined.filter(g =>
                  isNewLaunch(g) && !alreadyInTop.has(`${g.provider || ""}|${g.gameId || g.id}`)
                );
                const merged = [...topGames, ...freshInjections];
                // Sort: NEW-window games first, then popular-list order; within
                // each bucket the freshest launch date wins.
                merged.sort((a, b) => {
                  const aN = isNewLaunch(a) ? 0 : 1;
                  const bN = isNewLaunch(b) ? 0 : 1;
                  if (aN !== bN) return aN - bN;
                  return newLaunchTimestamp(b) - newLaunchTimestamp(a);
                });
                return merged.length > 0 ? (
                  <GridSection
                    title="Top Games"
                    icon={Crown}
                    games={merged}
                    totalCount={merged.length}
                    onGameClick={playGame}
                    onFavorite={toggleFavorite}
                    favorites={favorites}
                    visibleCount={6}
                    onViewAll={() => setActiveCategory("popular")}
                  />
                ) : null;
              })()}

              {/* ── Slot Games ────────────────────────── */}
              {(() => {
                const slotGames = allCombined.filter(g => classifyGame(g) === "slots");
                return slotGames.length > 0 ? (
                  <GridSection
                    title="Slot Games"
                    icon={Sparkles}
                    games={slotGames}
                    totalCount={slotGames.length}
                    onGameClick={playGame}
                    onFavorite={toggleFavorite}
                    favorites={favorites}
                    visibleCount={6}
                    onViewAll={() => setActiveCategory("slots")}
                  />
                ) : null;
              })()}

              {/* ── Fish Games ────────────────────────── */}
              {(() => {
                const fishGames = allCombined.filter(g => classifyGame(g) === "fish");
                return fishGames.length > 0 ? (
                  <GridSection
                    title="Fish Games"
                    icon={Fish}
                    games={fishGames}
                    totalCount={fishGames.length}
                    onGameClick={playGame}
                    onFavorite={toggleFavorite}
                    favorites={favorites}
                    visibleCount={6}
                    onViewAll={() => setActiveCategory("fish")}
                  />
                ) : null;
              })()}

              {/* ── Hold and Win ──────────────────────── */}
              {(() => {
                const holdWinGames = allCombined.filter(g => {
                  const name = (g.name || "").toLowerCase();
                  const tags = (g.tags || []).map(t => t.toLowerCase());
                  const cat = classifyGame(g);
                  // Exclude non-slot games (table, fish, crash, arcade)
                  if (cat === "table" || cat === "fish" || cat === "crash") return false;
                  // Match actual Hold & Win slot mechanics (specific phrases, not single words)
                  return (
                    name.includes("hold and win") ||
                    name.includes("hold & win") ||
                    name.includes("hold n win") ||
                    name.includes("hold the spin") ||
                    name.includes("hold the gold") ||
                    name.includes("hold n link") ||
                    name.includes("cash collect") ||
                    name.includes("link & win") ||
                    name.includes("link and win") ||
                    name.includes("lock and spin") ||
                    name.includes("lock & spin") ||
                    name.includes("lock it link") ||
                    name.includes("respin") ||
                    name.includes("re-spin") ||
                    name.includes("stacks") ||
                    name.includes("coins") ||
                    tags.includes("hold and win") ||
                    tags.includes("hold & win") ||
                    tags.includes("respin")
                  );
                });
                return holdWinGames.length > 0 ? (
                  <GridSection
                    title="Respin Games"
                    icon={Trophy}
                    games={holdWinGames}
                    totalCount={holdWinGames.length}
                    onGameClick={playGame}
                    onFavorite={toggleFavorite}
                    favorites={favorites}
                    visibleCount={6}
                    onViewAll={() => { setActiveCategory("slots"); setActiveSlotSubcategory("hold-win"); }}
                  />
                ) : null;
              })()}

              {/* ── Card & Arcade Games ───────────────────────── */}
              <div className="mb-8" data-testid="table-games-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Gamepad2 className="w-5 h-5 text-[#4d7cff]" />
                    <h3 className="text-base font-bold text-white tracking-tight">Card & Arcade Games</h3>
                  </div>
                  <button onClick={() => setActiveCategory("table")} className="text-emerald-400 text-sm font-semibold hover:underline" data-testid="view-all-table-games">View All</button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { name: "Crash", path: "/game/crash", id: "crash", desc: "Watch the multiplier grow!", grad: "from-purple-900/80 via-pink-900/60 to-red-900/80", icon: <Rocket className="w-6 h-6 text-white" /> },
                    { name: "Roulette", path: "/game/roulette", id: "roulette", desc: "Spin the wheel, win big", grad: "from-green-900/80 to-emerald-900/80", icon: <Star className="w-6 h-6 text-white" /> },
                    { name: "Texas Hold'em", path: "/game/poker", id: "poker", desc: "Play poker vs AI dealer", grad: "from-blue-900/80 to-indigo-900/80", icon: <Crown className="w-6 h-6 text-white" /> },
                  ].filter(g => !disabledGameIds.includes(g.id)).map(g => (
                    <div key={g.name} onClick={() => navigate(g.path)} className={`group cursor-pointer rounded-xl bg-gradient-to-br ${g.grad} border border-white/10 p-5 transition-all hover:scale-[1.02] hover:shadow-lg hover:border-white/20`} data-testid={`table-game-${g.name.toLowerCase().replace(/\s/g, '-')}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">{g.icon}</div>
                        <div>
                          <h4 className="text-white font-bold text-base">{g.name}</h4>
                          <p className="text-zinc-400 text-xs">{g.desc}</p>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <span className="text-[#4d7cff] text-xs font-medium group-hover:underline flex items-center gap-1">Play Now <Play className="w-3.5 h-3.5" fill="currentColor" /></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Top Arcade Games (Weekly MIXED pool: Mascot + Booming + Betsoft + Redwood)
                  Each game keeps its own provider — Betsoft titles like Gold Nugget Rush 2
                  route through Betsoft, not Mascot. ───────── */}
              {featuredData?.top_arcade_games?.games?.length > 0 && (
                <div className="mb-8" data-testid="top-arcade-games-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-400" />
                      <h3 className="text-base font-bold text-white tracking-tight">Top Arcade Games</h3>
                      <span className="px-2 py-0.5 text-[9px] font-bold bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30">WEEKLY</span>
                    </div>
                    <button onClick={() => { setActiveCategory("all"); setSelectedProvider("All"); setForceFlatView(true); }} className="text-emerald-400 text-sm font-semibold hover:underline" data-testid="view-all-top-arcade">View All</button>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-3">
                    {featuredData.top_arcade_games.games
                      .filter(g => !isProviderDisabled(g.provider || ""))
                      .slice(0, 10)
                      .map((game, idx) => {
                        const thumb = resolveCuratedThumb(game);
                        return (
                          <GridGameCard
                            key={game.id || game.gameId || idx}
                            game={{ ...game, image: thumb || game.image, thumbnail: thumb || game.thumbnail }}
                            onClick={() => playGame(game)}
                            onFavorite={toggleFavorite}
                            isFavorite={favorites.includes(game.id || game.gameId)}
                          />
                        );
                      })}
                  </div>
                </div>
              )}

              {/* ── Featured Games (4-day rotation) ─────── */}
              {featuredData?.featured_games && (
                <div className="mb-8" data-testid="featured-games-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <h3 className="text-base font-bold text-white tracking-tight">Featured Games</h3>
                      <span className="px-2 py-0.5 text-[9px] font-bold bg-[#4d7cff]/20 text-[#4d7cff] rounded-full border border-[#4d7cff]/30">REFRESHES EVERY 4 DAYS</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-3">
                    {[
                      ...(featuredData.featured_games.booming || []).map(g => ({ ...g, provider: "Booming Games" })),
                      ...(featuredData.featured_games.betsoft || []).map(g => {
                        const backendImg = g.image || g.thumbnail || g.imageUrl || "";
                        const trustBackend = typeof backendImg === "string" && (backendImg.startsWith("/games/betsoft/") || backendImg.startsWith("/api/game-assets/games/betsoft/"));
                        const img = trustBackend ? backendImg : getBetsoftGameImage(g.gameId || g.id, g.name);
                        return { ...g, provider: "Betsoft", image: img, thumbnail: img };
                      }),
                      ...(featuredData.featured_games.petersons || []).map(g => ({ ...g, provider: "Redwood" })),
                      ...(featuredData.featured_games.spinjoy  || []).map(g => ({ ...g, provider: "Spinjoy" })),
                    ].slice(0, 15).map((game, idx) => (
                      <GridGameCard
                        key={game.id || game.gameId || idx}
                        game={game}
                        onClick={() => playGame(game)}
                        onFavorite={toggleFavorite}
                        isFavorite={favorites.includes(game.id || game.gameId)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* ── All Games Grid ────────────────────── */}
              <div className="mb-8" data-testid="all-games-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Gamepad2 className="w-5 h-5 text-[#4d7cff]" />
                    <h3 className="text-base font-bold text-white tracking-tight">All Games</h3>
                  </div>
                  <button onClick={() => { setActiveCategory("all"); setForceFlatView(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-emerald-400 text-sm font-semibold hover:underline" data-testid="view-all-games-btn">View All</button>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {allCombined
                    .filter(g => !disabledGameIds.includes(g.id) && !disabledGameIds.includes(String(g.id)))
                    .filter(g => !isProviderDisabled(g.provider))
                    .slice(0, 18)
                    .map((game, idx) => (
                    <GridGameCard
                      key={game.id || idx}
                      game={game}
                      onClick={() => playGame(game)}
                      onFavorite={toggleFavorite}
                      isFavorite={favorites.includes(game.id)}
                    />
                  ))}
                </div>
                {allCombined.length > 18 && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={() => { setActiveCategory("all"); setForceFlatView(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className="px-8 py-2.5 bg-white/[0.05] border border-white/10 text-white text-sm font-semibold rounded-xl hover:bg-white/[0.08] hover:border-white/20 transition-all"
                      data-testid="show-more-btn"
                    >
                      Show More
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : filteredGames.length === 0 ? (
            /* ═══════ EMPTY STATE ═══════ */
            <div className="text-center py-24">
              <div className="w-16 h-16 rounded-2xl bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
                <Gamepad2 className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-lg font-bold mb-1 text-white">
                {activeCategory === "favorites" ? "No favorites yet"
                  : activeCategory === "featured" ? "No featured games yet"
                  : activeCategory === "top" ? "No TOP games yet"
                  : "No games found"}
              </h3>
              <p className="text-zinc-500 text-sm max-w-sm mx-auto">
                {activeCategory === "favorites" ? "Click the heart on any game to save it here."
                  : activeCategory === "featured" || activeCategory === "top" ? "Check back soon — our team is curating the best picks for you."
                  : "Try a different category or search term."}
              </p>
            </div>
          ) : (
            /* ═══════ FILTERED / SEARCH RESULTS GRID ═══════ */
            <div>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-white">
                  {forceFlatView && activeCategory === "all" && !searchQuery ? "All Games" :
                   activeCategory === "all" && searchQuery ? `Search: "${searchQuery}"` :
                   activeCategory === "slots" ? (activeSlotSubcategory === "all-slots" ? "All Slots" : SLOT_SUBCATEGORIES.find(s => s.id === activeSlotSubcategory)?.label || "Slots") :
                   CATEGORIES.find(c => c.id === activeCategory)?.label || "Games"}
                  <span className="ml-2 text-zinc-500 font-normal text-sm">({filteredGames.length})</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredGames.map((game, index) => (
                  <div
                    key={game.id || index}
                    onClick={() => playGame(game)}
                    data-testid={`game-card-${game.id}`}
                    className="group cursor-pointer"
                  >
                    <div className={`relative rounded-xl overflow-hidden bg-[#0a0510] border transition-all duration-300 hover:scale-[1.03] ${
                      game.provider === "JackRoyals Original" ? "border-[#4d7cff]/30 hover:border-[#4d7cff]/60 hover:shadow-[0_0_20px_rgba(77,124,255,0.12)]" :
                      game.isBetsoft ? "border-white/[0.06] hover:border-cyan-500/40 hover:shadow-[0_0_20px_rgba(6,182,212,0.1)]" :
                      game.isBooming ? "border-white/[0.06] hover:border-emerald-500/40 hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]" :
                      game.isPetersons ? "border-white/[0.06] hover:border-rose-500/40 hover:shadow-[0_0_20px_rgba(244,63,94,0.1)]" :
                      "border-white/[0.06] hover:border-[#4d7cff]/30 hover:shadow-[0_0_20px_rgba(77,124,255,0.08)]"
                    }`}>
                      <div className="aspect-square relative overflow-hidden">
                        <img src={game.thumbnail || game.image} alt={game.name} className={`w-full h-full ${game.isKA ? "object-cover" : "object-contain"} transition-transform duration-500 group-hover:scale-105`} loading="lazy"
                          onError={(e) => { 
                            if (game.isBetsoft && game.gameId) {
                              const catKey = GAME_CATEGORY_MAP[String(game.gameId)];
                              if (catKey && CATEGORY_IMAGES[catKey] && e.target.src !== CATEGORY_IMAGES[catKey]) {
                                e.target.src = CATEGORY_IMAGES[catKey];
                              } else if (!e.target.src.includes('_logo.png')) {
                                e.target.src = `https://swmd.bfrnd.net/media/games/${game.gameId}/${game.gameId}_logo.png`;
                              } else {
                                e.target.style.display = 'none';
                              }
                            } else { e.target.style.display = 'none'; }
                          }} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all bg-black/30">
                          <div className="w-12 h-12 rounded-full bg-[#4d7cff] flex items-center justify-center shadow-lg shadow-[#4d7cff]/40 scale-75 group-hover:scale-100 transition-transform duration-300">
                            <Play className="w-5 h-5 text-black ml-0.5" fill="black" />
                          </div>
                        </div>
                        {/* Badges */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          {game.jackpot_multiplier === 3 && <span className="px-2 py-0.5 bg-gradient-to-r from-[#4d7cff] to-[#7f5dff] text-black text-[9px] font-black rounded">3X</span>}
                          {game.isBetsoft && <span className="px-1.5 py-0.5 bg-cyan-500/90 text-white text-[9px] font-bold rounded backdrop-blur-sm">BETSOFT</span>}
                          {game.isPetersons && <span className="px-1.5 py-0.5 bg-rose-500/90 text-white text-[9px] font-bold rounded backdrop-blur-sm">P&S</span>}
                          {game.isBooming && <span className="px-1.5 py-0.5 bg-emerald-500/90 text-white text-[9px] font-bold rounded backdrop-blur-sm">BOOMING</span>}
                          {game.isMascot && <span className="px-1.5 py-0.5 bg-purple-500/90 text-white text-[9px] font-bold rounded backdrop-blur-sm">MASCOT</span>}
                          {game.badge === "NEW" && game.jackpot_multiplier !== 3 && <span className="px-1.5 py-0.5 bg-emerald-500/90 text-white text-[9px] font-bold rounded">NEW</span>}
                          {game.badge === "HOT" && <span className="px-1.5 py-0.5 bg-red-500/90 text-white text-[9px] font-bold rounded">HOT</span>}
                        </div>
                        {/* Favorite */}
                        <button onClick={(e) => { e.stopPropagation(); toggleFavorite(game.id); }} data-testid={`favorite-btn-${game.id}`}
                          className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all z-10 backdrop-blur-sm ${favorites.includes(game.id) ? "bg-red-500/80" : "bg-black/40 opacity-0 group-hover:opacity-100"}`}>
                          <Heart className={`w-3.5 h-3.5 ${favorites.includes(game.id) ? "fill-white text-white" : "text-white/80"}`} />
                        </button>
                        {/* Name — centered, provider label removed
                            (Feb 2026 clean-tiles polish). Badges already
                            telegraph the provider. */}
                        <div className="absolute bottom-0 left-0 right-0 p-2.5">
                          <p className="text-center text-white text-xs font-semibold truncate" data-no-sanitize>{game.name}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default GameLobby;

